import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Edit3, Loader2, ArrowUp, Share2, Copy, Mic, Home, Sparkles } from 'lucide-react';
import { useParams, useNavigate, Routes, Route, useLocation } from 'react-router-dom';

import FileUpload from './components/FileUpload';
import ProcessingStatus from './components/ProcessingStatus';
import SpeakerLabeling from './components/SpeakerLabeling';
// TranscriptEditor removed - Timeline View tab no longer used
import TTMLRankingEnhanced from './components/TTMLRankingEnhanced';
import ChatInterface from './components/ChatInterface';
import AgentChat from './components/AgentChat';
import Login from './components/Login';
import RequestAccess from './components/RequestAccess';
import OrphanSessionRecovery from './components/OrphanSessionRecovery';
import SessionBrowser from './components/SessionBrowser';
// REMOVED: PublicSessionView - legacy component that bypassed share token security
// Use /share/:shareToken routes instead which properly validate permissions
import PublicShareView from './components/PublicShareView';
import CentralAudioPlayer from './components/CentralAudioPlayer';
import AppHeader from './components/AppHeader';
import TabLayout, { TabId } from './components/TabLayout';
import VideoPlayer from './components/VideoPlayer';
import { VideoSource } from './components/VideoPlayer';
import ReadableTranscriptTab from './components/ReadableTranscriptTab';
import NarrativeTimeline from './components/NarrativeTimeline';
import DescriptionTab from './components/DescriptionTab';
import ContextFilesTab from './components/ContextFilesTab';
import QuestionsTab from './components/QuestionsTab';
import PublicQuestionExplorer from './components/PublicQuestionExplorer';
import VideoGallery from './components/VideoGallery';
import CommentsExplorer from './components/CommentsExplorer';
import AgentsPage from './components/AgentsPage';
import SchedulesPage from './components/SchedulesPage';
import PermissionSettingsPage from './components/PermissionSettingsPage';
import { FileText, BarChart3, MessageSquare, BookOpen, GitBranch, FileEdit, Paperclip, Youtube, Video } from 'lucide-react';

import { useAuth } from './services/AuthContext';
import { useDarkMode, DarkModeProvider } from './contexts/DarkModeContext';
import { AudioProvider, useAudio } from './contexts/AudioContext';
import { TopicVisualizationProvider } from './contexts/TopicVisualizationContext';
import { AudioUIProvider, useAudioUI } from './contexts/AudioUIContext';
import { AutoScrollProvider } from './contexts/AutoScrollContext';
import { ShareLinkProvider, useShareLink } from './contexts/ShareLinkContext';
import { IdleProvider } from './contexts/IdleContext';
import { deepgramService } from './services/deepgramService';
import { TranscriptProcessor } from './services/transcriptProcessor';
import { openRouterService, OpenRouterService, ModelPersistence } from './services/openRouterService';
import { sessionService } from './services/sessionService';
import { ensureOfflineShellReady } from './serviceWorkerRegistration';
import {
  getOfflineSession,
  getOfflineSessionIdForShare,
  isSessionAvailableOffline,
  listOfflineSessions,
  linkOfflineShareTokenToSession,
  removeOfflineSession,
  saveOfflineSession,
} from './services/offlineSessionStore';
import * as youtubeAnalyticsService from './services/youtubeAnalyticsService';
import { copyToClipboard } from './utils/clipboard';
import { debugLog } from './utils/debug';
import {
  buildImmediateOfflineDiagnosticsReport,
  clearOfflineDiagnostics,
  recordOfflineDiagnostic,
} from './utils/offlineDiagnostics';
import { fetchWithTimeout } from './utils/fetchWithTimeout';
import { startTitleGenerationSession } from './services/agentService';

import { useDevicePerformance } from './hooks/useDevicePerformance';

import {
  AudioFile,
  DropboxUploadDraft,
  ProcessingStatus as ProcessingStatusType,
  DeepgramResponse,
  SpeakerSegment,
  SpeakerLabels,
  AppSettings
} from './types';

import './App.css';

const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = isDevelopment ? 'http://localhost:7777' : '';

type AppState = 'ready' | 'processing' | 'labeling' | 'completed';
type CreditLimitNotice = {
  message: string;
  billing?: {
    totalCostUsd?: number | null;
    monthlyUsdLimit?: number | null;
    totalCreditsUsed?: number | null;
    monthlyCreditLimit?: number | null;
    period?: {
      periodId?: string | null;
    };
  } | null;
  sourceUrl?: string | null;
};

type OfflineToast = {
  message: string;
  tone: 'success' | 'error';
};

const CREDIT_LIMIT_EVENT = 'rescript-credit-limit';
const LIGHT_THEME_COLOR = '#232E3F';
const DARK_THEME_COLOR = '#111826';
const PUBLIC_SHARE_LOAD_TIMEOUT_MS = 12000;

declare global {
  interface Window {
    __rescriptCreditFetchPatched?: boolean;
  }
}

function formatMoney(value: number | null | undefined): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '$0.00';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
}

function formatCredits(value: number | null | undefined): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0.0';
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(numeric);
}

function formatStorageBytes(bytes?: number | null): string {
  const numeric = Number(bytes);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(numeric) / Math.log(1024)), units.length - 1);
  const value = numeric / (1024 ** exponent);
  return `${value.toFixed(value >= 100 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

function isUsableUrl(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function resolveCanonicalVideoSelection(
  sessionData: Record<string, any> | null | undefined,
  statusOverride?: Record<string, any> | null
): { youtubeUrl: string | null; url: string | null; source: VideoSource | null } {
  const explicitYoutubeCandidate = sessionData?.youtubeUrl;
  const explicitYoutubeLegacyCandidate = sessionData?.youtube_url;
  const sessionVideoCandidate = sessionData?.videoUrl;
  const sessionLegacyVideoCandidate = sessionData?.video_url;
  const linkedYoutubeCandidate = sessionData?.linkedYoutubeUrl;
  const playbackUrlCandidate = statusOverride?.playbackUrl;
  const playbackSourceCandidate = statusOverride?.playbackSource;

  const explicitYoutubeUrl = isUsableUrl(explicitYoutubeCandidate)
    ? explicitYoutubeCandidate
    : isUsableUrl(explicitYoutubeLegacyCandidate)
      ? explicitYoutubeLegacyCandidate
      : null;

  const fallbackUrl = isUsableUrl(playbackUrlCandidate)
    ? playbackUrlCandidate
    : isUsableUrl(sessionVideoCandidate)
      ? sessionVideoCandidate
      : isUsableUrl(sessionLegacyVideoCandidate)
        ? sessionLegacyVideoCandidate
        : isUsableUrl(linkedYoutubeCandidate)
          ? linkedYoutubeCandidate
          : null;

  const fallbackSource = playbackSourceCandidate
    || sessionData?.videoSource
    || sessionData?.video_source
    || (fallbackUrl ? 'youtube' : null);

  if (explicitYoutubeUrl) {
    return {
      youtubeUrl: explicitYoutubeUrl,
      url: explicitYoutubeUrl,
      source: 'youtube',
    };
  }

  return {
    youtubeUrl: explicitYoutubeUrl,
    url: fallbackUrl,
    source: fallbackSource as VideoSource | null,
  };
}

function resolvePlayerModeForVideoSource(source: VideoSource | null | undefined): 'audio' | 'youtube' {
  return source === 'youtube' ? 'youtube' : 'audio';
}

function getYouTubeThumbnailUrl(url: string | null | undefined): string | null {
  if (!isUsableUrl(url)) return null;

  const patterns = [
    /youtube\.com\/watch\?v=([^&]+)/,
    /youtu\.be\/([^?]+)/,
    /youtube\.com\/embed\/([^?]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) {
      return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
    }
  }

  return null;
}

// Main session component
function SessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [sharePermissions, setSharePermissions] = useState<any>(null);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [shareMetadata, setShareMetadata] = useState<any>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  // Cache full share data to avoid duplicate API calls
  const [cachedShareData, setCachedShareData] = useState<any>(null);
  const [canUseOfflineShareFallback, setCanUseOfflineShareFallback] = useState(false);

  // Parse parameters from URL
  const searchParams = new URLSearchParams(location.search);
  const timeParam = searchParams.get('t');
  const shareParam = searchParams.get('share');

  // Initialize loading state based on shareParam
  const [isLoadingShare, setIsLoadingShare] = useState(!!shareParam);

  useEffect(() => {
    console.log('[SESSION-PAGE] Share param:', shareParam);
    if (shareParam) {
      loadShareData(shareParam);
    }
  }, [shareParam]);

  const loadShareData = async (token: string) => {
    setIsLoadingShare(true);
    setShareError(null);
    setCanUseOfflineShareFallback(false);

    const tryOpenOfflineShare = async (): Promise<boolean> => {
      const mappedOfflineSessionId = token ? await getOfflineSessionIdForShare(token) : null;
      if (mappedOfflineSessionId) {
        if (sessionId && mappedOfflineSessionId !== sessionId) {
          navigate(`/session/${mappedOfflineSessionId}?share=${token}`, { replace: true });
          return true;
        }

        setShareToken(token);
        setShareError(null);
        setSharePermissions(null);
        setCachedShareData(null);
        setCanUseOfflineShareFallback(true);
        return true;
      }

      const canFallbackOffline = sessionId ? await isSessionAvailableOffline(sessionId) : false;
      if (!canFallbackOffline) {
        return false;
      }

      setShareToken(token);
      setShareError(null);
      setSharePermissions(null);
      setCachedShareData(null);
      setCanUseOfflineShareFallback(true);
      return true;
    };

    try {
      if (!navigator.onLine && await tryOpenOfflineShare()) {
        return;
      }

      const response = await fetchWithTimeout(
        `${API_BASE_URL}/api/public/share/${token}`,
        { cache: 'no-store' },
        PUBLIC_SHARE_LOAD_TIMEOUT_MS,
        'Timed out while loading the shared session'
      );
      if (response.ok) {
        const data = await response.json();
        // Cache the FULL response to avoid duplicate API calls
        setCachedShareData(data);
        setSharePermissions(data.permissions);
        setShareToken(token);
        setCanUseOfflineShareFallback(false);
        if (data.shareLink) {
          setShareMetadata(data.shareLink);
        }
      } else {
        // Share link is invalid, disabled, or otherwise unavailable
        if (response.status === 404) {
          setShareError('Share link not found');
        } else if (response.status === 410) {
          setShareError('This share link is not currently available');
        } else if (response.status === 403) {
          setShareError('This share link has been disabled');
        } else {
          setShareError('Invalid share link');
        }
        setSharePermissions(null);
        setCachedShareData(null);
      }
    } catch (error) {
      console.error('Failed to load share data:', error);
      if (await tryOpenOfflineShare()) {
        return;
      }

      setShareError('Failed to load share link');
      setSharePermissions(null);
      setCachedShareData(null);
      setCanUseOfflineShareFallback(false);
    } finally {
      setIsLoadingShare(false);
    }
  };
  
  console.log('[SESSION-PAGE] Render state:', {
    shareParam,
    isLoadingShare,
    sharePermissions,
    shareError,
    canUseOfflineShareFallback,
  });
  
  // If we have a share parameter, we need to wait for permissions to load
  if (shareParam) {
    if (isLoadingShare) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFB52B]" />
        </div>
      );
    }
    
    if (!canUseOfflineShareFallback && (shareError || !sharePermissions)) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2 text-red-600">
              {shareError || 'Invalid share link'}
            </h1>
            <p className="text-gray-600">
              The shared session you're looking for is not available.
            </p>
          </div>
        </div>
      );
    }
  }
  
  if (shareParam && sharePermissions) {
    return (
      <ShareLinkProvider permissions={sharePermissions} shareToken={shareToken || undefined} metadata={shareMetadata}>
        <TranscriberApp sessionId={sessionId} navigate={navigate} timeParam={timeParam} isLoadingShare={false} cachedShareData={cachedShareData} />
      </ShareLinkProvider>
    );
  }
  
  // Pass loading state if we have a share param but no permissions yet
  // Always wrap in ShareLinkProvider to avoid context issues
  return (
    <ShareLinkProvider shareToken={shareParam || undefined} metadata={shareMetadata || undefined}>
      <TranscriberApp sessionId={sessionId} navigate={navigate} timeParam={timeParam} isLoadingShare={isLoadingShare} />
    </ShareLinkProvider>
  );
}

// Main transcriber component (extracted from App)
function TranscriberApp({ sessionId, navigate, timeParam, isLoadingShare = false, cachedShareData = null }: { sessionId?: string; navigate: any; timeParam?: string | null; isLoadingShare?: boolean; cachedShareData?: any }) {
  // console.error('[App] TranscriberApp rendered - sessionId:', sessionId);
  
  const { token, user, login, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const {
    loadAudio,
    seekTo,
    play,
    currentTime,
    isPlaying,
    volume,
    playbackRate,
    isLoading: isAudioLoading,
    duration,
    setPlayerMode,
    setYouTubeTime,
    setYouTubeDuration,
    setYouTubePlaying,
    mediaSeekRequestId,
    mediaSeekRequestTime,
  } = useAudio();
  const { setCursorSyncEnabled } = useAudioUI();
  const { permissions: sharePermissions, shareToken } = useShareLink();
  const perfSettings = useDevicePerformance();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [authStatusChecked, setAuthStatusChecked] = useState(false);
  const [isForbidden, setIsForbidden] = useState(false);
  const [appState, setAppState] = useState<AppState>('ready');
  const [selectedFile, setSelectedFile] = useState<AudioFile | null>(null);
  const [selectedYouTubeUrl, setSelectedYouTubeUrl] = useState<string | null>(null);
  const [selectedDropboxUpload, setSelectedDropboxUpload] = useState<DropboxUploadDraft | null>(null);
  const [pendingShowAssignment, setPendingShowAssignment] = useState({
    showSlug: 'mlst',
    showTitle: 'Machine Learning Street Talk',
  });
  const [pendingFamilyAssignment, setPendingFamilyAssignment] = useState<{
    familySlug: string;
    familyTitle: string;
    familyVersionNumber: number | null;
    seedSessionId: string;
    seedSessionTitle: string;
  }>({
    familySlug: '',
    familyTitle: '',
    familyVersionNumber: null,
    seedSessionId: '',
    seedSessionTitle: '',
  });
  const [isYouTubeMode, setIsYouTubeMode] = useState<boolean>(false);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatusType>({
    status: 'idle',
    message: 'Ready to transcribe'
  });
  
  // Transcription results
  const [transcriptionData, setTranscriptionData] = useState<DeepgramResponse | null>(null);
  const [playbackTranscriptionData, setPlaybackTranscriptionData] = useState<DeepgramResponse | null>(null);
  const [transcriptionChecksum, setTranscriptionChecksum] = useState<string | null>(null);
  const [speakerSegments, setSpeakerSegments] = useState<Map<number, SpeakerSegment[]>>(new Map());
  const [distinctSpeakers, setDistinctSpeakers] = useState<number[]>([]);
  const [finalTranscript, setFinalTranscript] = useState<string>('');
  const [currentTranscript, setCurrentTranscript] = useState<string>('');
  const [speakerLabels, setSpeakerLabels] = useState<SpeakerLabels>({});

  // AI settings
  const [supportedModels, setSupportedModels] = useState<string[]>([]);
  const [selectedAIModel, setSelectedAIModel] = useState<string>('');
  const [initialTimeParam, setInitialTimeParam] = useState<string | null>(timeParam || null);
  
  // Narrative/Topic Map data
  const [narrativeData, setNarrativeData] = useState<any>(null);
  
  // Update initialTimeParam if timeParam changes
  useEffect(() => {
    if (timeParam) {
      console.log('[SESSION] Time parameter updated:', timeParam);
      setInitialTimeParam(timeParam);
    }
  }, [timeParam]);
  
  const [shouldOpenReadableTab, setShouldOpenReadableTab] = useState<boolean>(false);
  // Set default tab - for initial load only
  const getInitialTab = (): TabId => {
    // If we have a sessionId, try to load the saved tab from localStorage
    if (sessionId) {
      const savedTab = localStorage.getItem(`activeTab_${sessionId}`);
      if (savedTab && ['readable', 'ttml', 'chat', 'agent', 'narrative', 'description', 'context', 'questions'].includes(savedTab)) {
        console.log('[TAB-PERSISTENCE] Loading saved tab from localStorage:', savedTab);
        return savedTab as TabId;
      }
    }
    
    console.log('[TAB-PERSISTENCE] No saved tab found, defaulting to readable');
    return 'readable'; // Default to Transcript tab
  };
  
  const [activeTabId, setActiveTabId] = useState<TabId>(getInitialTab());
  const previousActiveTabRef = useRef<TabId>(activeTabId);
  const [currentTranscriptTopicTitle, setCurrentTranscriptTopicTitle] = useState<string | null>(null);
  const [hasOfflineSessionSnapshot, setHasOfflineSessionSnapshot] = useState<boolean | null>(sessionId ? null : false);
  const [isOfflineSessionMode, setIsOfflineSessionMode] = useState(false);
  const [isOfflineAvailable, setIsOfflineAvailable] = useState(false);
  const [isOfflineActionPending, setIsOfflineActionPending] = useState(false);
  const [offlineToast, setOfflineToast] = useState<OfflineToast | null>(null);
  const [offlineActionStatusLabel, setOfflineActionStatusLabel] = useState<string | null>(null);
  const offlineAudioUrlRef = useRef<string | null>(null);
  const offlineToastTimeoutRef = useRef<number | null>(null);
  const shareHasNarrativeData = !sharePermissions
    || sharePermissions.hasNarrativeData === true
    || !!narrativeData;
  const shareHasContextFiles = !sharePermissions
    || sharePermissions.hasContextFiles === true
    || Number(sharePermissions?.contextFileCount || 0) > 0;
  const availableTabIds = useMemo<TabId[]>(() => {
    if (isOfflineSessionMode) {
      return ['readable', 'description'];
    }

    if (sharePermissions) {
      const tabs: TabId[] = [];

      if (sharePermissions.transcriptsEnabled) tabs.push('readable');
      if (sharePermissions.chatMode !== 'DISABLED') tabs.push('chat');
      if (sharePermissions.rankingEnabled && shareHasNarrativeData) tabs.push('narrative');
      if (sharePermissions.descriptionEnabled !== false) tabs.push('description');
      if (sharePermissions.contextFilesEnabled === true && shareHasContextFiles) tabs.push('context');
      if (sharePermissions.questionsEnabled === true) tabs.push('questions');

      return tabs;
    }

    const tabs: TabId[] = ['ttml', 'chat', 'readable', 'narrative', 'description', 'context', 'questions'];
    if (token) {
      tabs.splice(2, 0, 'agent');
    }
    return tabs;
  }, [isOfflineSessionMode, shareHasContextFiles, shareHasNarrativeData, sharePermissions, token]);
  
  // Update tab for share permissions - only for share links
  useEffect(() => {
    if (sharePermissions && availableTabIds) {
      // For share links, default to the first available tab
      const [newTab = 'readable'] = availableTabIds;
      
      console.log('[TAB-PERSISTENCE] Setting tab for share link:', newTab);
      setActiveTabId(newTab);
    }
  }, [availableTabIds, sharePermissions, sessionId]);

  useEffect(() => {
    if (availableTabIds.length === 0) {
      return;
    }

    if (!availableTabIds.includes(activeTabId)) {
      setActiveTabId(availableTabIds[0]);
    }
  }, [activeTabId, availableTabIds]);
  
  // Save active tab to localStorage when it changes (only for sessions)
  useEffect(() => {
    if (sessionId && activeTabId) {
      console.log('[TAB-PERSISTENCE] Saving active tab to localStorage:', activeTabId);
      localStorage.setItem(`activeTab_${sessionId}`, activeTabId);
    }
  }, [sessionId, activeTabId]);

  useEffect(() => {
    const previousTab = previousActiveTabRef.current;
    if (activeTabId === 'readable' && previousTab !== 'readable') {
      setCursorSyncEnabled(false);
    }
    previousActiveTabRef.current = activeTabId;
  }, [activeTabId, setCursorSyncEnabled]);

  // Listen for navigate-to-agent events from refinement modals
  useEffect(() => {
    const handleNavigateToAgent = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      console.log('[TAB-NAV] Navigating to agent tab', detail);
      setPendingAgentSessionId(detail?.agentSessionId || null);
      setActiveTabId('agent');
    };
    window.addEventListener('navigate-to-agent', handleNavigateToAgent);
    return () => window.removeEventListener('navigate-to-agent', handleNavigateToAgent);
  }, []);

  const [generatedTtmlFiles, setGeneratedTtmlFiles] = useState<Array<{ name: string; content: string; checksum?: string }>>([]);
  const [previewTtmlId, setPreviewTtmlId] = useState<string | null>(null);

  // Session management
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(sessionId || null);
  const [pendingAgentSessionId, setPendingAgentSessionId] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [isLoadingSession, setIsLoadingSession] = useState<boolean>(!!sessionId);
  const [currentSessionData, setCurrentSessionData] = useState<any>(null);
  const selectedTtmlChecksum = useMemo(() => {
    const selectedId = currentSessionData?.selectedTtmlId;
    if (!selectedId) return null;
    const matchedFile = generatedTtmlFiles.find((file) =>
      (file as any).checksum === selectedId
      || (file as any).filename === selectedId
      || file.name === selectedId
    );
    return matchedFile?.checksum || selectedId || null;
  }, [currentSessionData?.selectedTtmlId, generatedTtmlFiles]);
  const selectedTranscriptChecksum = useMemo(() => (
    currentSessionData?.selectedTranscriptChecksum
    || currentSessionData?.selected_transcript_checksum
    || currentSessionData?.transcriptionChecksum
    || null
  ), [
    currentSessionData?.selectedTranscriptChecksum,
    currentSessionData?.selected_transcript_checksum,
    currentSessionData?.transcriptionChecksum,
  ]);
  const [sessionInvalidationVersion, setSessionInvalidationVersion] = useState(0);
  
  // Session title management
  const [sessionTitle, setSessionTitle] = useState<string>('');
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [isSavingTitle, setIsSavingTitle] = useState<boolean>(false);
  const [isGeneratingTitle, setIsGeneratingTitle] = useState<boolean>(false);
  const autoTitleAgentStartedRef = useRef<string | null>(null);
  const [showStickyHeader, setShowStickyHeader] = useState<boolean>(false);
  const [isTranscriptFullscreen, setIsTranscriptFullscreen] = useState(false);

  // Search matches from transcript tab - used for timeline markers
  const [transcriptSearchMatches, setTranscriptSearchMatches] = useState<any[]>([]);

  const pickPositiveDuration = (...values: Array<number | null | undefined>) => {
    for (const value of values) {
      const numericValue = Number(value);
      if (Number.isFinite(numericValue) && numericValue > 0) {
        return numericValue;
      }
    }
    return undefined;
  };

  // Video player state (unified for YouTube/Dropbox)
  const [linkedYoutubeUrl, setLinkedYoutubeUrl] = useState<string | null>(null);
  const [linkedVideoSource, setLinkedVideoSource] = useState<VideoSource | null>(null);
  const [youtubeLinkUrl, setYoutubeLinkUrl] = useState<string | null>(null);
  const [isYoutubePlayerVisible, setIsYoutubePlayerVisible] = useState(false);
  const [isYoutubePlayerFloating, setIsYoutubePlayerFloating] = useState(false);
  const [youtubePlayerWidth, setYoutubePlayerWidth] = useState(384); // Default 384px (lg:w-96)
  const [isResizingYoutubePanel, setIsResizingYoutubePanel] = useState(false);

  const [viewportMetrics, setViewportMetrics] = useState(() => {
    if (typeof window === 'undefined') {
      return { height: 0, topOffset: 0, bottomOffset: 0 };
    }

    const viewport = window.visualViewport;
    const height = viewport?.height ?? window.innerHeight;
    const topOffset = Math.max(0, viewport?.offsetTop ?? 0);
    const bottomOffset = Math.max(0, window.innerHeight - height - topOffset);

    return { height, topOffset, bottomOffset };
  });

  // Production metadata state
  const [dropboxVideoUrl, setDropboxVideoUrl] = useState<string | null>(null);
  const [videoFilename, setVideoFilename] = useState<string | null>(null);
  const [dropboxDrpUrl, setDropboxDrpUrl] = useState<string | null>(null);
  const [timelineName, setTimelineName] = useState<string | null>(null);
  const [localVideoPath, setLocalVideoPath] = useState<string | null>(null);
  const [productionNotes, setProductionNotes] = useState<string | null>(null);
  const [publishedToHomepage, setPublishedToHomepage] = useState(false);
  const hasSelectedInput = !!(selectedFile || selectedYouTubeUrl || selectedDropboxUpload);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateViewportMetrics = () => {
      const viewport = window.visualViewport;
      const height = viewport?.height ?? window.innerHeight;
      const topOffset = Math.max(0, viewport?.offsetTop ?? 0);
      const bottomOffset = Math.max(0, window.innerHeight - height - topOffset);

      setViewportMetrics({ height, topOffset, bottomOffset });
    };

    updateViewportMetrics();

    const viewport = window.visualViewport;
    window.addEventListener('resize', updateViewportMetrics);
    viewport?.addEventListener('resize', updateViewportMetrics);
    viewport?.addEventListener('scroll', updateViewportMetrics);

    return () => {
      window.removeEventListener('resize', updateViewportMetrics);
      viewport?.removeEventListener('resize', updateViewportMetrics);
      viewport?.removeEventListener('scroll', updateViewportMetrics);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !perfSettings.isMobile) {
      return;
    }

    const hasEditableAncestor = (target: EventTarget | null): boolean => (
      target instanceof HTMLElement
      && !!target.closest('input, textarea, select, [contenteditable="true"]')
    );

    const hasScrollableAncestor = (target: EventTarget | null): boolean => {
      let node = target instanceof HTMLElement ? target : null;

      while (node) {
        const style = window.getComputedStyle(node);
        const overflowY = style.overflowY;
        const overflowX = style.overflowX;
        const canScrollY = /(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight;
        const canScrollX = /(auto|scroll|overlay)/.test(overflowX) && node.scrollWidth > node.clientWidth;

        if (node.dataset.allowTouchScroll === 'true' || canScrollY || canScrollX) {
          return true;
        }

        node = node.parentElement;
      }

      return false;
    };

    const preventGesture = (event: Event) => {
      if (event.cancelable) {
        event.preventDefault();
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!event.cancelable) {
        return;
      }

      if (event.touches.length > 1) {
        event.preventDefault();
        return;
      }

      if (hasEditableAncestor(event.target) || hasScrollableAncestor(event.target)) {
        return;
      }

      event.preventDefault();
    };

    document.addEventListener('gesturestart', preventGesture, { passive: false });
    document.addEventListener('gesturechange', preventGesture, { passive: false });
    document.addEventListener('gestureend', preventGesture, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });

    return () => {
      document.removeEventListener('gesturestart', preventGesture);
      document.removeEventListener('gesturechange', preventGesture);
      document.removeEventListener('gestureend', preventGesture);
      document.removeEventListener('touchmove', handleTouchMove, true);
    };
  }, [perfSettings.isMobile]);

  // Handle autoplay with time parameter once media is loaded
  // This handles both audio-only and video modes
  useEffect(() => {
    if (!initialTimeParam) {
      return;
    }

    // For video mode, we need the video player to report duration
    // For audio mode, we need the audio to be loaded
    const hasVideo = !!linkedYoutubeUrl;

    // If we have a video but duration is 0, wait for video player to report duration
    if (hasVideo && (!duration || duration === 0)) {
      console.log('[SESSION] Waiting for video player to report duration...');
      return;
    }

    // If audio mode and still loading, wait
    if (!hasVideo && (isAudioLoading || !duration || duration === 0)) {
      return;
    }

    // Parse time parameter (e.g., "56m30s" -> seconds)
    const parseTimeParam = (param: string): number => {
      const match = param.match(/(\d+)m(\d+)s/);
      if (match) {
        const minutes = parseInt(match[1], 10);
        const seconds = parseInt(match[2], 10);
        return minutes * 60 + seconds;
      }
      return 0;
    };

    const targetTime = parseTimeParam(initialTimeParam);
    console.log('[SESSION] Media loaded, attempting autoplay:', {
      targetTime,
      duration,
      isAudioLoading,
      hasVideo,
      timeParam: initialTimeParam
    });

    if (targetTime > 0 && targetTime < duration) {
      // Longer delay for video mode to ensure player is ready
      const delay = hasVideo ? 500 : 100;
      setTimeout(async () => {
        console.log('[SESSION] Seeking to time:', targetTime, 'seconds (video mode:', hasVideo, ')');
        seekTo(targetTime);
        // Don't force tab change - let the user's saved preference take precedence

        // Autoplay after seeking
        try {
          await play();
          console.log('[SESSION] Autoplay started successfully');
        } catch (error) {
          console.log('[SESSION] Autoplay blocked:', error);
        }

        // Clear the time parameter to prevent re-seeking
        setInitialTimeParam(null);
      }, delay);
    }
  }, [initialTimeParam, isAudioLoading, duration, seekTo, play, linkedYoutubeUrl]);

  // YouTube Analytics retention data
  const [retentionData, setRetentionData] = useState<youtubeAnalyticsService.RetentionData | null>(null);
  const [gapAnalysisData, setGapAnalysisData] = useState<any>(null);
  const [filteredGapClusters, setFilteredGapClusters] = useState<any[] | null>(null);
  const [nonClusteredGaps, setNonClusteredGaps] = useState<any[] | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setHasOfflineSessionSnapshot(false);
      return;
    }

    let cancelled = false;
    setHasOfflineSessionSnapshot(null);

    isSessionAvailableOffline(sessionId)
      .then((available) => {
        if (!cancelled) {
          setHasOfflineSessionSnapshot(available);
        }
      })
      .catch((error) => {
        console.warn('[OFFLINE] Failed to check session snapshot:', error);
        if (!cancelled) {
          setHasOfflineSessionSnapshot(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    const targetSessionId = currentSessionId || sessionId;
    if (!targetSessionId) {
      setIsOfflineAvailable(false);
      return;
    }

    let cancelled = false;

    isSessionAvailableOffline(targetSessionId)
      .then((available) => {
        if (!cancelled) {
          setIsOfflineAvailable(available);
        }
      })
      .catch((error) => {
        console.warn('[OFFLINE] Failed to sync availability state:', error);
        if (!cancelled) {
          setIsOfflineAvailable(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentSessionId, sessionId]);

  useEffect(() => {
    if (!shareToken || !currentSessionId || !isOfflineAvailable) {
      return;
    }

    linkOfflineShareTokenToSession(shareToken, currentSessionId).catch((error) => {
      console.warn('[OFFLINE] Failed to link share token to offline session:', error);
    });
  }, [currentSessionId, isOfflineAvailable, shareToken]);

  const revokeOfflineAudioUrl = useCallback(() => {
    if (offlineAudioUrlRef.current) {
      URL.revokeObjectURL(offlineAudioUrlRef.current);
      offlineAudioUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      revokeOfflineAudioUrl();
    };
  }, [revokeOfflineAudioUrl]);

  const applyLoadedSessionData = useCallback((sessionData: any, options: { offlineAudioUrl?: string | null; offlineMode?: boolean } = {}) => {
    const offlineMode = options.offlineMode === true;
    const effectiveSessionData = offlineMode
      ? {
          ...sessionData,
          audioUrl: options.offlineAudioUrl || null,
          linkedYoutubeUrl: null,
          videoUrl: null,
          videoSource: null,
        }
      : sessionData;

    setIsOfflineSessionMode(offlineMode);
    setCurrentSessionData(effectiveSessionData);
    setTranscriptionData(effectiveSessionData.transcriptionData || null);
    setPlaybackTranscriptionData(effectiveSessionData.transcriptionData || null);
    setTranscriptionChecksum(effectiveSessionData.transcriptionChecksum || null);
    setSpeakerLabels(effectiveSessionData.speakerLabels || {});
    setFinalTranscript(effectiveSessionData.finalTranscript || '');
    setCurrentTranscript(effectiveSessionData.currentTranscript || effectiveSessionData.finalTranscript || '');
    setSpeakerSegments(new Map(
      Object.entries(effectiveSessionData.speakerSegments || {})
        .filter(([key]) => !Number.isNaN(parseInt(key, 10)))
        .map(([key, value]) => [parseInt(key, 10), value as SpeakerSegment[]])
    ));
    setDistinctSpeakers(Array.isArray(effectiveSessionData.distinctSpeakers) ? effectiveSessionData.distinctSpeakers : []);
    setGeneratedTtmlFiles(effectiveSessionData.generatedTtmlFiles || []);
    setCurrentSessionId(effectiveSessionData.sessionId);
    setShareUrl(sessionService.generateSessionUrl(effectiveSessionData.sessionId));
    setPreviewTtmlId(null);
    setFilteredGapClusters(null);
    setNonClusteredGaps(null);
    setGapAnalysisData(null);
    setNarrativeData(offlineMode ? null : effectiveSessionData._narrativeData || null);
    setRetentionData(null);

    const title = effectiveSessionData.title || effectiveSessionData.filename || 'Untitled Session';
    setSessionTitle(title);

    const resolvedVideoSelection = offlineMode
      ? { youtubeUrl: null, url: null, source: null }
      : resolveCanonicalVideoSelection(effectiveSessionData);

    if (resolvedVideoSelection.url) {
      setLinkedYoutubeUrl(resolvedVideoSelection.url);
      setLinkedVideoSource(resolvedVideoSelection.source);
      setYoutubeLinkUrl(resolvedVideoSelection.youtubeUrl);
      setIsYoutubePlayerVisible(true);
      setPlayerMode(resolvePlayerModeForVideoSource(resolvedVideoSelection.source));
    } else {
      setLinkedYoutubeUrl(null);
      setLinkedVideoSource(null);
      setYoutubeLinkUrl(resolvedVideoSelection.youtubeUrl);
      setIsYoutubePlayerVisible(false);
      setPlayerMode('audio');
    }

    if (offlineMode) {
      setDropboxVideoUrl(null);
      setVideoFilename(null);
      setDropboxDrpUrl(null);
      setTimelineName(null);
      setLocalVideoPath(null);
      setProductionNotes(null);
      setPublishedToHomepage(false);
      setSelectedDropboxUpload(null);
      setSelectedYouTubeUrl(null);
      setIsYouTubeMode(false);
      setCurrentTranscriptTopicTitle(null);
    } else {
      setDropboxVideoUrl(effectiveSessionData.dropboxVideoUrl || null);
      setVideoFilename(effectiveSessionData.videoFilename || null);
      setDropboxDrpUrl(effectiveSessionData.dropboxDrpUrl || null);
      setTimelineName(effectiveSessionData.timelineName || null);
      setLocalVideoPath(effectiveSessionData.localVideoPath || null);
      setProductionNotes(effectiveSessionData.productionNotes || null);
      setPublishedToHomepage(!!effectiveSessionData.publishedToHomepage);

      if (effectiveSessionData.isYouTubeMode && effectiveSessionData.youtubeUrl) {
        setSelectedYouTubeUrl(effectiveSessionData.youtubeUrl);
        setIsYouTubeMode(true);
        setSelectedFile(null);
        setSelectedDropboxUpload(null);
      } else if (effectiveSessionData.dropboxVideoUrl) {
        setSelectedDropboxUpload({
          dropboxVideoUrl: effectiveSessionData.dropboxVideoUrl,
          localVideoPath: effectiveSessionData.localVideoPath || '',
          productionNotes: effectiveSessionData.productionNotes || '',
          timelineName: effectiveSessionData.timelineName || '',
          dropboxDrpUrl: effectiveSessionData.dropboxDrpUrl || '',
          videoFilename: effectiveSessionData.videoFilename || effectiveSessionData.filename,
          autoPrepareVideoSource: true,
        });
        setSelectedFile(null);
        setIsYouTubeMode(false);
        setSelectedYouTubeUrl(null);
      } else {
        const placeholderFile = new File([], effectiveSessionData.filename);
        setSelectedFile({
          name: effectiveSessionData.filename,
          file: placeholderFile,
          size: 0,
        });
        setSelectedDropboxUpload(null);
        setIsYouTubeMode(false);
        setSelectedYouTubeUrl(null);
      }
    }

    if (offlineMode) {
      const placeholderFile = new File([], effectiveSessionData.filename || title);
      setSelectedFile({
        name: effectiveSessionData.filename || title,
        file: placeholderFile,
        size: 0,
      });
    }

    if (effectiveSessionData.finalTranscript || effectiveSessionData.currentTranscript) {
      setAppState('completed');
      setProcessingStatus({
        status: 'completed',
        message: offlineMode ? 'Offline session loaded.' : 'Session loaded successfully!',
      });
    }

    if (effectiveSessionData.audioUrl && (!resolvedVideoSelection.url || resolvedVideoSelection.source === 'file')) {
      loadAudio(effectiveSessionData.audioUrl);
    }
  }, [loadAudio, setPlayerMode]);

  const loadOfflineSnapshot = useCallback(async (sessionIdToLoad: string) => {
    const offlineRecord = await getOfflineSession(sessionIdToLoad);
    if (!offlineRecord) {
      return false;
    }

    revokeOfflineAudioUrl();
    const offlineAudioUrl = offlineRecord.audioBlob
      ? URL.createObjectURL(offlineRecord.audioBlob as Blob)
      : null;

    if (offlineAudioUrl) {
      offlineAudioUrlRef.current = offlineAudioUrl;
    }

    applyLoadedSessionData(offlineRecord.sessionData, {
      offlineAudioUrl,
      offlineMode: true,
    });
    return true;
  }, [applyLoadedSessionData, revokeOfflineAudioUrl]);

  // Handler for video URL changes from AppHeader (unified for YouTube/Dropbox)
  const handleVideoUrlChange = useCallback((url: string | null, source: VideoSource | null) => {
    setLinkedYoutubeUrl(url);
    setLinkedVideoSource(source);
    setYoutubeLinkUrl(source === 'youtube' ? url : null);
    if (url) {
      setIsYoutubePlayerFloating(false);
      setIsYoutubePlayerVisible(true);
      setPlayerMode(resolvePlayerModeForVideoSource(source));
      if (source === 'file') {
        const audioUrl = shareToken
          ? `/api/public/share/${shareToken}/audio`
          : currentSessionData?.audioUrl;
        if (audioUrl) {
          loadAudio(audioUrl, {
            startTime: currentTime,
            shouldPlay: isPlaying,
          });
        }
      }
    } else {
      setIsYoutubePlayerVisible(false);
      setPlayerMode('audio'); // Switch back to audio mode
    }
  }, [currentSessionData?.audioUrl, currentTime, isPlaying, loadAudio, setPlayerMode, shareToken]);

  const handleYoutubeUrlChange = useCallback((url: string | null) => {
    setYoutubeLinkUrl(url);
  }, []);
  const effectiveVideoUrl = linkedYoutubeUrl;
  const effectiveVideoSource = linkedVideoSource;

  const toggleVideoPanelVisibility = useCallback((forceVisible?: boolean) => {
    const nextVisible = typeof forceVisible === 'boolean' ? forceVisible : !isYoutubePlayerVisible;
    const resumeTime = currentTime;
    const shouldResumePlayback = isPlaying;
    if (perfSettings.isMobile) {
      setIsYoutubePlayerFloating(false);
    }
    setIsYoutubePlayerVisible(nextVisible);

    if (nextVisible) {
      const nextMode = resolvePlayerModeForVideoSource(effectiveVideoSource || null);
      setPlayerMode(nextMode);
      if (nextMode === 'audio') {
        const audioUrl = shareToken
          ? `/api/public/share/${shareToken}/audio`
          : currentSessionData?.audioUrl;
        if (audioUrl) {
          loadAudio(audioUrl, {
            startTime: resumeTime,
            shouldPlay: shouldResumePlayback,
          });
        }
      }
      return;
    }

    setPlayerMode('audio');
    const audioUrl = shareToken
      ? `/api/public/share/${shareToken}/audio`
      : currentSessionData?.audioUrl;
    if (audioUrl) {
      console.log('[SESSION] Video collapsed - loading MP3 audio:', audioUrl);
      loadAudio(audioUrl, {
        startTime: resumeTime,
        shouldPlay: shouldResumePlayback,
      });
    }
  }, [currentSessionData?.audioUrl, currentTime, isPlaying, isYoutubePlayerVisible, loadAudio, perfSettings.isMobile, setPlayerMode, shareToken]);

  useEffect(() => {
    if (!perfSettings.isMobile || activeTabId === 'readable' || !isYoutubePlayerVisible) {
      return;
    }

    toggleVideoPanelVisibility(false);
  }, [activeTabId, isYoutubePlayerVisible, perfSettings.isMobile, toggleVideoPanelVisibility]);

  // Determine if video player should be visible based on effective video URL
  const shouldShowVideoPlayer = useMemo(() => {
    return isYoutubePlayerVisible && !!effectiveVideoUrl;
  }, [effectiveVideoUrl, isYoutubePlayerVisible]);

  const ignoreHostedVideoPlaybackSignal = useCallback(() => {}, []);

  // Function to reload current session data
  const reloadSessionData = useCallback(async () => {
    if (!currentSessionId || !token) return;
    
    try {
      const sessionData = await sessionService.getSession(currentSessionId, token);
      setCurrentSessionData(sessionData);
      setGeneratedTtmlFiles(sessionData.generatedTtmlFiles || []);
      setSessionTitle(sessionData.title || sessionData.filename || 'Untitled Session');
      setYoutubeLinkUrl(sessionData.youtubeUrl || null);
      setDropboxVideoUrl(sessionData.dropboxVideoUrl || null);
      setVideoFilename(sessionData.videoFilename || null);
      setDropboxDrpUrl(sessionData.dropboxDrpUrl || null);
      setTimelineName(sessionData.timelineName || null);
      setLocalVideoPath(sessionData.localVideoPath || null);
      setProductionNotes(sessionData.productionNotes || null);
      setPublishedToHomepage(!!sessionData.publishedToHomepage);

      const nextVideoSelection = resolveCanonicalVideoSelection(sessionData);
      setLinkedYoutubeUrl(nextVideoSelection.url);
      setLinkedVideoSource(nextVideoSelection.source);
      setYoutubeLinkUrl(nextVideoSelection.youtubeUrl);
      if (!nextVideoSelection.url) {
        setIsYoutubePlayerVisible(false);
        setPlayerMode('audio');
      }

      const latestNarrativeData = await sessionService.getNarrativeData(currentSessionId, shareToken || token);
      setNarrativeData(latestNarrativeData || null);

      console.log('[SESSION] Session data reloaded after external update');
    } catch (error) {
      console.error('[SESSION] Failed to reload session data:', error);
    }
  }, [currentSessionId, shareToken, token, setPlayerMode]);

  const showOfflineToast = useCallback((message: string, tone: OfflineToast['tone']) => {
    setOfflineToast({ message, tone });
    if (offlineToastTimeoutRef.current) {
      window.clearTimeout(offlineToastTimeoutRef.current);
    }
    offlineToastTimeoutRef.current = window.setTimeout(() => {
      setOfflineToast(null);
      offlineToastTimeoutRef.current = null;
    }, 3200);
  }, []);

  const updateOfflineActionStatus = useCallback((message: string, details?: Record<string, unknown>) => {
    setOfflineActionStatusLabel(message);
    recordOfflineDiagnostic('info', message, details);
  }, []);

  const finalizeOfflineShellSetup = useCallback(async (): Promise<boolean> => {
    if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
      return true;
    }

    const shellStatus = await ensureOfflineShellReady();
    recordOfflineDiagnostic('info', 'Checked offline shell readiness', shellStatus);

    if (shellStatus.ready) {
      return true;
    }

    recordOfflineDiagnostic('info', 'Service worker is not yet ready for offline navigation');
    return false;
  }, []);

  useEffect(() => {
    return () => {
      if (offlineToastTimeoutRef.current) {
        window.clearTimeout(offlineToastTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyOfflineDiagnostics = useCallback(async () => {
    try {
      const report = buildImmediateOfflineDiagnosticsReport({
        sessionId: currentSessionId || null,
        shareToken: shareToken || null,
        pending: isOfflineActionPending,
        offlineAvailable: isOfflineAvailable,
        offlineMode: isOfflineSessionMode,
        sessionTitle: sessionTitle || currentSessionData?.title || null,
        currentStatus: offlineActionStatusLabel,
      });

      const copied = await copyToClipboard(report);
      showOfflineToast(
        copied ? 'Offline diagnostics copied to clipboard.' : 'Failed to copy offline diagnostics.',
        copied ? 'success' : 'error'
      );
    } catch (error) {
      console.error('[OFFLINE] Failed to copy offline diagnostics:', error);
      showOfflineToast('Failed to copy offline diagnostics.', 'error');
    }
  }, [
    currentSessionData?.title,
    currentSessionId,
    isOfflineActionPending,
    isOfflineAvailable,
    isOfflineSessionMode,
    offlineActionStatusLabel,
    sessionTitle,
    shareToken,
    showOfflineToast,
  ]);

  const handleToggleOfflineAvailability = useCallback(async () => {
    if (!currentSessionId) {
      return;
    }

    try {
      clearOfflineDiagnostics();
      recordOfflineDiagnostic('info', 'Started offline availability update', {
        sessionId: currentSessionId,
        shareToken: shareToken || null,
        audioUrl: currentSessionData?.audioUrl || null,
        online: navigator.onLine,
        hadServiceWorkerController: !!navigator.serviceWorker?.controller,
      });
      setIsOfflineActionPending(true);

      if (isOfflineAvailable) {
        updateOfflineActionStatus('Removing offline copy', { sessionId: currentSessionId });
        await removeOfflineSession(currentSessionId);
        setIsOfflineAvailable(false);
        window.dispatchEvent(new CustomEvent('rescript-offline-sessions-updated'));
        recordOfflineDiagnostic('info', 'Offline copy removed', { sessionId: currentSessionId });
        showOfflineToast('Offline copy removed from this device.', 'success');
        return;
      }

      if (!currentSessionData?.audioUrl) {
        throw new Error('This session does not currently expose an audio file for offline playback.');
      }

      updateOfflineActionStatus('Requesting audio for offline playback', {
        audioUrl: currentSessionData.audioUrl,
      });
      const audioResponse = await fetch(currentSessionData.audioUrl);
      if (!audioResponse.ok) {
        throw new Error('Failed to download the session audio for offline playback.');
      }

      const contentLengthHeader = audioResponse.headers.get('Content-Length');
      const audioBytes = contentLengthHeader ? Number(contentLengthHeader) : NaN;
      recordOfflineDiagnostic('info', 'Audio response headers received', {
        status: audioResponse.status,
        contentType: audioResponse.headers.get('Content-Type'),
        contentLength: Number.isFinite(audioBytes) ? audioBytes : null,
      });
      updateOfflineActionStatus(
        Number.isFinite(audioBytes)
          ? `Preparing ${formatStorageBytes(audioBytes)} of audio for offline playback`
          : 'Preparing audio for offline playback',
        {
          audioBytes: Number.isFinite(audioBytes) ? audioBytes : null,
        }
      );
      if (Number.isFinite(audioBytes) && 'storage' in navigator && typeof navigator.storage?.estimate === 'function') {
        try {
          const storageEstimate = await navigator.storage.estimate();
          const availableBytes = typeof storageEstimate.quota === 'number' && typeof storageEstimate.usage === 'number'
            ? Math.max(0, storageEstimate.quota - storageEstimate.usage)
            : null;

          console.log('[OFFLINE] Storage estimate before save:', {
            audioBytes,
            quota: storageEstimate.quota ?? null,
            usage: storageEstimate.usage ?? null,
            availableBytes,
          });

          if (availableBytes !== null && audioBytes > availableBytes) {
            throw new Error(
              `This device appears to have only ${formatStorageBytes(availableBytes)} free for offline storage, but the audio download is ${formatStorageBytes(audioBytes)}.`
            );
          }
        } catch (error) {
          if (error instanceof Error) {
            throw error;
          }
          console.warn('[OFFLINE] Failed to estimate browser storage:', error);
        }
      }

      const offlineDuration = pickPositiveDuration(
        transcriptionData?.metadata?.duration,
        currentSessionData.duration,
        retentionData?.videoDurationSeconds
      ) || 0;
      const sessionSnapshot = {
        sessionId: currentSessionId,
        transcriptionChecksum: currentSessionData.transcriptionChecksum || transcriptionChecksum || '',
        filename: currentSessionData.filename || selectedFile?.name || sessionTitle || 'Untitled Session',
        title: sessionTitle || currentSessionData.title || currentSessionData.filename || 'Untitled Session',
        thumbnailUrl: getYouTubeThumbnailUrl(currentSessionData.youtubeUrl || currentSessionData.youtube_url) || null,
        isYouTubeMode: false,
        youtubeUrl: currentSessionData.youtubeUrl || undefined,
        transcriptionData: transcriptionData || currentSessionData.transcriptionData || null,
        speakerLabels: speakerLabels || currentSessionData.speakerLabels || {},
        finalTranscript: finalTranscript || currentSessionData.finalTranscript || '',
        currentTranscript: currentTranscript || currentSessionData.currentTranscript || finalTranscript || '',
        speakerSegments: currentSessionData.speakerSegments || Object.fromEntries(speakerSegments),
        distinctSpeakers: currentSessionData.distinctSpeakers || distinctSpeakers,
        generatedTtmlFiles: (generatedTtmlFiles || []).map((file: any) => ({
          ...file,
          content: file.content || '',
        })),
        selectedTtmlId: currentSessionData.selectedTtmlId || null,
        youtubeDescription: currentSessionData.youtubeDescription || '',
        _comments: currentSessionData._comments || [],
        createdAt: currentSessionData.createdAt,
        updatedAt: currentSessionData.updatedAt,
        duration: offlineDuration,
      };

      updateOfflineActionStatus('Saving offline copy to browser storage', {
        sessionId: currentSessionId,
      });
      await saveOfflineSession(
        sessionSnapshot,
        audioResponse,
        {
          shareToken,
          thumbnailUrl: sessionSnapshot.thumbnailUrl,
          onProgress: (message, details) => {
            updateOfflineActionStatus(message, details);
          },
        }
      );
      updateOfflineActionStatus('Verifying offline copy', { sessionId: currentSessionId });
      const [isNowAvailableOffline, offlineSessions] = await Promise.all([
        isSessionAvailableOffline(currentSessionId),
        listOfflineSessions(),
      ]);
      const isVisibleInLibrary = offlineSessions.some((session) => session.sessionId === currentSessionId);

      if (!isNowAvailableOffline || !isVisibleInLibrary) {
        throw new Error('Offline save did not verify in local storage. Please try again.');
      }

      setIsOfflineAvailable(true);
      recordOfflineDiagnostic('info', 'Offline copy verified in local storage', {
        sessionId: currentSessionId,
        isVisibleInLibrary,
      });
      window.dispatchEvent(new CustomEvent('rescript-offline-sessions-updated'));

      updateOfflineActionStatus('Finalizing offline setup', { sessionId: currentSessionId });
      const offlineShellReady = await finalizeOfflineShellSetup();
      if (offlineShellReady) {
        recordOfflineDiagnostic('info', 'Offline setup finished successfully', { sessionId: currentSessionId });
        showOfflineToast('Session saved for offline use on this device.', 'success');
        return;
      }

      showOfflineToast(
        'Offline copy saved, but Safari is still preparing offline page loading in the background.',
        'success'
      );
    } catch (error) {
      console.error('[OFFLINE] Failed to update offline availability:', error);
      recordOfflineDiagnostic('error', 'Offline availability update failed', error);
      showOfflineToast(
        error instanceof Error ? error.message : 'Failed to update offline availability.',
        'error'
      );
    } finally {
      setIsOfflineActionPending(false);
      setOfflineActionStatusLabel(null);
    }
  }, [
    currentSessionData,
    currentSessionId,
    currentTranscript,
    distinctSpeakers,
    finalTranscript,
    generatedTtmlFiles,
    isOfflineAvailable,
    retentionData?.videoDurationSeconds,
    selectedFile?.name,
    sessionTitle,
    shareToken,
    finalizeOfflineShellSetup,
    showOfflineToast,
    updateOfflineActionStatus,
    speakerLabels,
    speakerSegments,
    transcriptionChecksum,
    transcriptionData,
  ]);

  useEffect(() => {
    const handleSessionRefreshNeeded = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (detail.sessionId && detail.sessionId !== currentSessionId) {
        return;
      }
      void reloadSessionData();
    };

    window.addEventListener('rescript-session-refresh-needed', handleSessionRefreshNeeded as EventListener);
    return () => {
      window.removeEventListener('rescript-session-refresh-needed', handleSessionRefreshNeeded as EventListener);
    };
  }, [currentSessionId, reloadSessionData]);

  useEffect(() => {
    if (!currentSessionId || !token || sharePermissions) {
      setSessionInvalidationVersion(0);
      return undefined;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const result = await sessionService.getInvalidations(currentSessionId, sessionInvalidationVersion, token);
        if (cancelled) return;
        if (result.version !== sessionInvalidationVersion) {
          setSessionInvalidationVersion(result.version);
        }
        if (!result.hasUpdates) {
          return;
        }
        window.dispatchEvent(new CustomEvent('rescript-session-invalidated', {
          detail: {
            sessionId: currentSessionId,
            scopes: result.scopes,
            version: result.version,
          },
        }));
        await reloadSessionData();
      } catch (error) {
        if (!cancelled) {
          console.error('[SESSION] Failed to poll invalidations:', error);
        }
      }
    };

    void poll();
    const interval = window.setInterval(() => {
      void poll();
    }, 4000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [currentSessionId, reloadSessionData, sessionInvalidationVersion, sharePermissions, token]);
  
  // Function to update TTML files and save to session
  const updateGeneratedTtmlFiles = useCallback(async (newFiles: Array<{ name: string; content: string; checksum?: string }>) => {
    console.log('[TTML-UPDATE] Starting updateGeneratedTtmlFiles with', newFiles.length, 'files');
    console.log('[TTML-UPDATE] Files details:', newFiles.map(f => ({
      name: f.name,
      checksum: f.checksum,
      contentLength: f.content?.length || 0,
      hasStats: !!(f as any).validationStats
    })));
    
    // Get current files to merge with
    const currentFiles = generatedTtmlFiles;
    
    // Create a map of existing files by checksum
    const existingByChecksum = new Map();
    currentFiles.forEach(file => {
      if (file.checksum) {
        existingByChecksum.set(file.checksum, file);
      }
    });
    
    // Merge new files, avoiding duplicates
    const mergedFiles = [...currentFiles];
    for (const newFile of newFiles) {
      // Check if file with same checksum already exists
      if (!newFile.checksum || !existingByChecksum.has(newFile.checksum)) {
        mergedFiles.push(newFile);
      } else {
        console.log('[TTML-UPDATE] Skipping duplicate file with checksum:', newFile.checksum);
      }
    }
    
    console.log('[TTML-UPDATE] Merged files count:', mergedFiles.length);
    
    // Update state
    setGeneratedTtmlFiles(mergedFiles);
    
    // Update session with all TTML files (merged)
    if (currentSessionId && token) {
      try {
        console.log('[TTML-UPDATE] Updating session', currentSessionId, 'with', mergedFiles.length, 'TTML files');
        const updateResult = await sessionService.updateSession({
          sessionId: currentSessionId,
          generatedTtmlFiles: mergedFiles
        }, token);
        console.log('[TTML-UPDATE] Session update result:', updateResult);
        console.log('[SESSION] Updated session with TTML files');
      } catch (error) {
        console.error('[SESSION] Failed to update TTML files in session:', error);
      }
    } else {
      console.warn('[TTML-UPDATE] Cannot update session - missing sessionId or token:', {
        hasSessionId: !!currentSessionId,
        hasToken: !!token
      });
    }
  }, [currentSessionId, token, generatedTtmlFiles]);

  // Handle new TTML revision created from CentralAudioPlayer (add/edit/delete reference)
  const handleTtmlRevisionCreated = useCallback(async (revision: {
    checksum: string;
    version: number;
    parentChecksum: string;
    rootChecksum: string;
    generatedAt: string;
    model: string;
    revisionNotes: string | null;
    editedBy: string;
    content?: string;
  }) => {
    console.log('[TTML-REVISION] New revision created:', {
      checksum: revision.checksum.substring(0, 8),
      version: revision.version,
      parentChecksum: revision.parentChecksum.substring(0, 8)
    });

    // Create a new TTML file entry for the revision
    const newFile = {
      name: `v${revision.version} (manual edit)`,
      content: revision.content || '',
      checksum: revision.checksum,
      version: revision.version,
      parentChecksum: revision.parentChecksum,
      rootChecksum: revision.rootChecksum,
      generatedAt: revision.generatedAt,
      model: revision.model,
      revisionNotes: revision.revisionNotes,
      editedBy: revision.editedBy,
      editType: 'manual',  // Mark as manual edit
      isSelected: true  // Mark as selected
    };

    // Add to TTML files list
    const updatedFiles = [...generatedTtmlFiles, newFile];

    // Mark the new version as selected, unmark others
    const filesWithSelection = updatedFiles.map(f => ({
      ...f,
      isSelected: f.checksum === revision.checksum
    }));

    setGeneratedTtmlFiles(filesWithSelection);

    // Update the preview to show the new version
    setPreviewTtmlId(revision.checksum);

    // Update session with the new TTML files and select the new version
    if (currentSessionId && token) {
      try {
        console.log('[TTML-REVISION] Updating session with new revision and selecting it');
        await sessionService.updateSession({
          sessionId: currentSessionId,
          generatedTtmlFiles: filesWithSelection,
          selectedTtmlId: revision.checksum
        }, token);

        // Also update currentSessionData so the selection persists
        if (currentSessionData) {
          setCurrentSessionData({
            ...currentSessionData,
            selectedTtmlId: revision.checksum
          });
        }

        console.log('[TTML-REVISION] Session updated successfully');
      } catch (error) {
        console.error('[TTML-REVISION] Failed to update session:', error);
      }
    }
  }, [currentSessionId, token, generatedTtmlFiles, currentSessionData]);

  // Settings
  const [settings, setSettings] = useState<AppSettings>({
    model: 'nova-3',
    language: 'en-US',
    smartFormat: true,
    diarization: true,
    punctuation: true,
    utterances: true,
    timeInterval: 15
  });

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          await deepgramService.healthAuth(token);
          setIsAuthorized(true);
        } catch (error) {
          setIsAuthorized(false);
        } finally {
          setAuthStatusChecked(true);
        }
      } else {
        setIsAuthorized(false);
        setAuthStatusChecked(true);
      }
    };

    verifyToken();
  }, [token]);

  // Handle YouTube panel resize
  useEffect(() => {
    if (!isResizingYoutubePanel) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      // Calculate new width based on mouse X position, with min/max constraints
      const newWidth = Math.min(Math.max(e.clientX, 280), 800); // Min 280px, Max 800px
      setYoutubePlayerWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizingYoutubePanel(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingYoutubePanel]);

  // Load chat models lazily so non-chat session work does not touch the OpenRouter path.
  useEffect(() => {
    if (isOfflineSessionMode) return;
    if (activeTabId !== 'chat') return;

    // Skip loading models if not authorized and not share access
    if (!isAuthorized && !sharePermissions) return;

    // Always load AI models - users can use BYOK (bring your own key) even on public shares
    const loadAIModels = async () => {
      try {
        console.log('Loading AI models from OpenRouter...');
        // Use the new dynamic OpenRouter models endpoint
        const response = await openRouterService.getOpenRouterModels(token || undefined);
        const modelIds = response.models.map(m => m.id);
        console.log('AI models loaded:', modelIds.length, 'models');
        setSupportedModels(modelIds);

        if (modelIds.length > 0) {
          // Check for persisted model first
          const persistedModel = ModelPersistence.getSelectedModel();
          if (persistedModel && modelIds.includes(persistedModel)) {
            console.log('Using persisted model:', persistedModel);
            setSelectedAIModel(persistedModel);
          } else {
            setSelectedAIModel(modelIds[0]);
          }
        }
      } catch (error) {
        console.error('Failed to load AI models:', error);
        const persistedModel = ModelPersistence.getSelectedModel();
        const fallbackModels = persistedModel ? [persistedModel] : [];
        console.log('Using fallback models:', fallbackModels);
        setSupportedModels(fallbackModels);
        setSelectedAIModel(fallbackModels[0] || '');
      }
    };
    loadAIModels();
  }, [activeTabId, isAuthorized, isOfflineSessionMode, token, sharePermissions]);

  // Persist model selection when it changes
  useEffect(() => {
    if (selectedAIModel) {
      ModelPersistence.setSelectedModel(selectedAIModel);
    }
  }, [selectedAIModel]);

  // Load session data if sessionId is provided
  useEffect(() => {
    const loadSession = async () => {
      if (!sessionId) {
        setIsLoadingSession(false);
        return;
      }

      if (!sharePermissions && !authStatusChecked) {
        setIsLoadingSession(true);
        return;
      }

      const canLoadRemoteSession = !!sharePermissions || (!!isAuthorized && !!token);

      if (!canLoadRemoteSession) {
        if (!hasOfflineSessionSnapshot) {
          setIsLoadingSession(false);
          return;
        }

        try {
          setIsLoadingSession(true);
          const loadedOffline = await loadOfflineSnapshot(sessionId);
          if (!loadedOffline) {
            setIsLoadingSession(false);
          }
          return;
        } catch (error) {
          console.error('[SESSION] Failed to load offline snapshot:', error);
          setIsLoadingSession(false);
          return;
        }
      }

      try {
        console.log('[SESSION] Loading session:', sessionId);
        setIsLoadingSession(true);
        revokeOfflineAudioUrl();
        
        // Load session data based on access type
        let sessionData: any;
        if (sharePermissions && shareToken) {
          // Use cached data from SessionPage if available (avoids duplicate API call)
          let shareData: any;
          if (cachedShareData) {
            console.log('[SESSION] Using cached share data (no duplicate API call)');
            shareData = cachedShareData;
          } else {
            // Fallback: Load via public share endpoint (should rarely happen)
            console.log('[SESSION] No cached data, fetching from API');
            const response = await fetchWithTimeout(
              `${API_BASE_URL}/api/public/share/${shareToken}`,
              { cache: 'no-store' },
              PUBLIC_SHARE_LOAD_TIMEOUT_MS,
              'Timed out while loading the shared session'
            );
            if (!response.ok) {
              throw new Error('Failed to load shared session');
            }
            shareData = await response.json();
          }
          sessionData = shareData.session;

          console.log('[SESSION] Share data received:', {
            hasTranscriptionData: !!sessionData.transcriptionData,
            transcriptionDataType: typeof sessionData.transcriptionData,
            transcriptionDataKeys: sessionData.transcriptionData ? Object.keys(sessionData.transcriptionData) : [],
            hasSpeakerLabels: !!sessionData.speakerLabels,
            permissions: shareData.permissions
          });
          
          const normalizedShareTranscriptionData = sessionData.transcriptionData?.deepgram || sessionData.transcriptionData || null;
          const normalizedShareDuration = pickPositiveDuration(
            sessionData.duration,
            normalizedShareTranscriptionData?.metadata?.duration
          );

          // Transform the session data to match the expected format
          // Note: speaker_segments and distinct_speakers are already parsed by the public API
          sessionData = {
            ...sessionData,
            sessionId: sessionData.session_id,
            title: sessionData.title, // Include the title field
            filename: sessionData.filename,
            isYouTubeMode: sessionData.is_youtube_mode,
            youtubeUrl: sessionData.youtube_url,
            linkedYoutubeUrl: sessionData.youtube_url || sessionData.video_url, // Canonical explicit YouTube link first
            videoUrl: sessionData.youtube_url || sessionData.video_url, // Canonical playback URL
            videoSource: sessionData.youtube_url ? 'youtube' : (sessionData.video_source || null), // Video source type
            finalTranscript: sessionData.final_transcript,
            // current_transcript removed from API response - use final_transcript as fallback
            currentTranscript: sessionData.current_transcript || sessionData.final_transcript,
            speakerSegments: sessionData.speaker_segments || {},
            distinctSpeakers: sessionData.distinct_speakers || [],
            transcriptionData: normalizedShareTranscriptionData, // Normalize revision-wrapper payloads to raw Deepgram
            speakerLabels: sessionData.speakerLabels || {},
            // Merge _ttmlContent into the selected TTML file so DescriptionTab can parse topics/references
            generatedTtmlFiles: (sessionData.generated_ttml_files || []).map((f: any) => ({
              ...f,
              // Add content to the selected TTML file from _ttmlContent
              content: (f.checksum === sessionData.selected_ttml_id || f.id === sessionData.selected_ttml_id || f.isSelected)
                ? sessionData._ttmlContent
                : f.content
            })),
            audioUrl: sessionData.s3_original_audio_key ? `/api/public/share/${shareToken}/audio` : null,
            selectedTtmlId: sessionData.selected_ttml_id,
            transcriptionChecksum:
              sessionData.selectedTranscriptChecksum
              || sessionData.selected_transcript_checksum
              || sessionData.transcriptionChecksum
              || sessionData.transcription_checksum
              || null,
            selectedTranscriptChecksum: sessionData.selectedTranscriptChecksum || sessionData.selected_transcript_checksum || null,
            youtubeDescription: sessionData.youtube_description || '', // For Description tab
            // Pre-loaded comments from CDN bundle (for static content)
            _comments: sessionData._comments || [],
            _fromCdn: shareData._fromCdn || false,
            // Duration for edit calculations
            duration: normalizedShareDuration || 0
          };
          setNarrativeData(sessionData._narrativeData || null);

          // Note: Audio loading is handled below in the common code path for both share and authenticated sessions

          // Note: transcriptionData and speakerLabels are already included in the initial share response
          // No need to fetch them separately
          
          // Note: TTML files are now included in the initial session data from the public share endpoint
        } else {
          // Load via authenticated endpoint
          sessionData = await sessionService.getSession(sessionId, token!);
        }
        applyLoadedSessionData(sessionData);
        console.log('[SESSION] Session loaded successfully');

        // Auto-run gap analysis if session has transcription data and is authenticated (not a share link)
        if (sessionData.transcriptionData && token && !sharePermissions) {
          OpenRouterService.analyzeGaps(sessionData.sessionId, { minGapSeconds: 0.3, relativeThreshold: 1.5 }, token)
            .then(result => {
              setGapAnalysisData(result);
              console.log('[GAP] Auto-analysis complete:', result.summary?.clusters, 'clusters');
            })
            .catch(err => console.log('[GAP] Auto-analysis failed (non-critical):', err.message));
        }
      } catch (error) {
        console.error('[SESSION] Failed to load session:', error);
        try {
          const loadedOffline = await loadOfflineSnapshot(sessionId);
          if (loadedOffline) {
            return;
          }
        } catch (offlineError) {
          console.error('[SESSION] Offline fallback also failed:', offlineError);
        }
        setProcessingStatus({
          status: 'error',
          message: 'Failed to load session. The session may not exist or you may not have access.'
        });
        // Redirect to home after a delay
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } finally {
        setIsLoadingSession(false);
      }
    };

    loadSession();
  }, [
    sessionId,
    isAuthorized,
    token,
    navigate,
    sharePermissions,
    shareToken,
    cachedShareData,
    authStatusChecked,
    hasOfflineSessionSnapshot,
    loadOfflineSnapshot,
    revokeOfflineAudioUrl,
    applyLoadedSessionData,
  ]);

  const refreshCurrentVideoSourceStatus = useCallback(async (sessionIdToRefresh: string) => {
    if (!token) return;
    try {
      const status = await sessionService.getVideoSourceStatus(sessionIdToRefresh, token);
      setCurrentSessionData((prev: any) => prev ? ({
        ...prev,
        videoSourceStatus: status,
        videoUrl: resolveCanonicalVideoSelection(prev, status).url,
        videoSource: resolveCanonicalVideoSelection(prev, status).source,
        videoFileHash: status.videoMetadata.hash || prev.videoFileHash,
        videoFileSize: status.videoMetadata.fileSize ?? prev.videoFileSize,
        videoFrameRate: status.videoMetadata.frameRate ?? prev.videoFrameRate,
        videoDuration: status.videoMetadata.duration ?? prev.videoDuration,
      }) : prev);

      if (status.dropboxVideoUrl !== undefined) {
        setDropboxVideoUrl(status.dropboxVideoUrl || null);
      }
      if (status.localVideoPath !== undefined) {
        setLocalVideoPath(status.localVideoPath || null);
      }

      const resolvedVideoSelection = resolveCanonicalVideoSelection(currentSessionData, status);

      if (resolvedVideoSelection.url && resolvedVideoSelection.source) {
        setLinkedYoutubeUrl(resolvedVideoSelection.url);
        setLinkedVideoSource(resolvedVideoSelection.source);
        setYoutubeLinkUrl(resolvedVideoSelection.youtubeUrl);
        if (isYoutubePlayerVisible) {
          setPlayerMode(resolvePlayerModeForVideoSource(resolvedVideoSelection.source));
          if (resolvedVideoSelection.source === 'file') {
            const audioUrl = currentSessionData?.audioUrl;
            if (audioUrl) {
              loadAudio(audioUrl, {
                startTime: currentTime,
                shouldPlay: isPlaying,
              });
            }
          }
        }
      } else {
        setLinkedYoutubeUrl(null);
        setLinkedVideoSource(null);
        setYoutubeLinkUrl(resolvedVideoSelection.youtubeUrl);
        setIsYoutubePlayerVisible(false);
        setPlayerMode('audio');
      }
    } catch (error) {
      console.error('[SESSION] Failed to refresh video source status:', error);
    }
  }, [currentSessionData, currentTime, isPlaying, isYoutubePlayerVisible, loadAudio, setPlayerMode, token]);

  useEffect(() => {
    const shouldPoll = !!(
      currentSessionId
      && token
      && dropboxVideoUrl
      && (
        currentSessionData?.videoSourceStatus?.proxy?.status === 'processing'
        || (!linkedYoutubeUrl && currentSessionData?.videoSourceStatus?.sourceAvailable)
      )
    );

    if (!shouldPoll || !currentSessionId) {
      return;
    }

    void refreshCurrentVideoSourceStatus(currentSessionId);
    const interval = window.setInterval(() => {
      void refreshCurrentVideoSourceStatus(currentSessionId);
    }, 20000);

    return () => window.clearInterval(interval);
  }, [
    currentSessionData?.videoSourceStatus?.proxy?.status,
    currentSessionData?.videoSourceStatus?.sourceAvailable,
    currentSessionId,
    dropboxVideoUrl,
    linkedYoutubeUrl,
    refreshCurrentVideoSourceStatus,
    token,
  ]);

  // Load YouTube retention data when session has a YouTube URL (not for Dropbox/other sources)
  useEffect(() => {
    const loadRetentionData = async () => {
      console.log('[RETENTION] useEffect triggered:', { currentSessionId, linkedYoutubeUrl, linkedVideoSource });
      // Only fetch retention data for YouTube videos, not Dropbox or other sources
      if (!currentSessionId || !linkedYoutubeUrl || linkedVideoSource !== 'youtube') {
        console.log('[RETENTION] Skipping - not a YouTube video:', {
          currentSessionId: !!currentSessionId,
          linkedYoutubeUrl: !!linkedYoutubeUrl,
          linkedVideoSource
        });
        setRetentionData(null);
        return;
      }

      try {
        console.log('[RETENTION] Loading retention data for YouTube session:', currentSessionId);
        const data = await youtubeAnalyticsService.getRetentionData(
          currentSessionId,
          token || undefined,
          shareToken || undefined
        );
        if (data) {
          console.log('[RETENTION] Loaded retention data:', data.retentionData.length, 'points');
          setRetentionData(data);
        } else {
          console.log('[RETENTION] No retention data available');
          setRetentionData(null);
        }
      } catch (error) {
        console.error('[RETENTION] Failed to load retention data:', error);
        setRetentionData(null);
      }
    };

    loadRetentionData();
  }, [currentSessionId, linkedYoutubeUrl, linkedVideoSource, token, shareToken]);

  // Load narrative data when session changes
  useEffect(() => {
    const loadNarrativeData = async () => {
      if (!currentSessionId) {
        setNarrativeData(null);
        return;
      }

      try {
        console.log('[NARRATIVE] Loading narrative data for session:', currentSessionId);
        const data = await sessionService.getNarrativeData(currentSessionId, shareToken || token || undefined);
        if (data) {
          console.log('[NARRATIVE] Loaded narrative data:', data);
          setNarrativeData(data);
        } else {
          console.log('[NARRATIVE] No narrative data available for session');
          setNarrativeData(null);
        }
      } catch (error) {
        console.error('[NARRATIVE] Failed to load narrative data:', error);
        setNarrativeData(null);
      }
    };

    loadNarrativeData();
  }, [currentSessionId, token, shareToken]);

  // Update document title and meta description based on session
  useEffect(() => {
    console.log('[TITLE-UPDATE] Effect triggered:', {
      sessionTitle,
      currentSessionId,
      hasSharePermissions: !!sharePermissions
    });

    const sessionSocialImage = currentSessionData?.customThumbnailUrl
      || (currentSessionData as any)?.custom_thumbnail_url
      || currentSessionData?.resolvedThumbnailUrl
      || (currentSessionData as any)?.thumbnail_url
      || getYouTubeThumbnailUrl(
      currentSessionData?.youtubeUrl
      || currentSessionData?.youtube_url
      || youtubeLinkUrl
      || linkedYoutubeUrl
    )
      || 'https://files.rescript.info/mlst-og-image.png';
    const sessionSocialImageAlt = sessionSocialImage === 'https://files.rescript.info/mlst-og-image.png'
      ? 'Machine Learning Street Talk ReScript archive'
      : `${sessionTitle || 'MLST session'} thumbnail`;
    
    if (sessionTitle && (currentSessionId || sharePermissions)) {
      // For session pages, show session name only
      document.title = `${sessionTitle} | MLST ReScript`;
      console.log('[TITLE-UPDATE] Set page title to:', sessionTitle);
      
      const sessionDescription = sharePermissions
        ? `${sessionTitle} | Public MLST transcript, episode notes, and downloadable PDF edition.`
        : `${sessionTitle} | MLST ReScript session`;
      const pageUrl = sharePermissions && shareToken
        ? `https://app.rescript.info/share/${shareToken}`
        : window.location.href;
      const metaDescription = document.querySelector('meta[name="description"]');
      const ogTitle = document.querySelector('meta[property="og:title"]');
      const ogDescription = document.querySelector('meta[property="og:description"]');
      const ogUrl = document.querySelector('meta[property="og:url"]');
      const ogImage = document.querySelector('meta[property="og:image"]');
      const ogImageAlt = document.querySelector('meta[property="og:image:alt"]');
      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      const twitterDescription = document.querySelector('meta[name="twitter:description"]');
      const twitterUrl = document.querySelector('meta[name="twitter:url"]');
      const twitterImage = document.querySelector('meta[name="twitter:image"]');
      const twitterImageAlt = document.querySelector('meta[name="twitter:image:alt"]');
      const canonical = document.querySelector('link[rel="canonical"]');

      if (metaDescription) metaDescription.setAttribute('content', sessionDescription);
      if (ogTitle) ogTitle.setAttribute('content', `${sessionTitle} | MLST ReScript`);
      if (ogDescription) ogDescription.setAttribute('content', sessionDescription);
      if (ogUrl) ogUrl.setAttribute('content', pageUrl);
      if (ogImage) ogImage.setAttribute('content', sessionSocialImage);
      if (ogImageAlt) ogImageAlt.setAttribute('content', sessionSocialImageAlt);
      if (twitterTitle) twitterTitle.setAttribute('content', `${sessionTitle} | MLST ReScript`);
      if (twitterDescription) twitterDescription.setAttribute('content', sessionDescription);
      if (twitterUrl) twitterUrl.setAttribute('content', pageUrl);
      if (twitterImage) twitterImage.setAttribute('content', sessionSocialImage);
      if (twitterImageAlt) twitterImageAlt.setAttribute('content', sessionSocialImageAlt);
      if (canonical) canonical.setAttribute('href', pageUrl);
    } else {
      // For home page or other pages
      document.title = 'Machine Learning Street Talk | ReScript Editorial Transcript Archive';
      console.log('[TITLE-UPDATE] Set page title to default: Machine Learning Street Talk | ReScript Editorial Transcript Archive');
      
      const defaultDescription = 'Machine Learning Street Talk ReScript is the editorial transcript archive for long-form AI conversations: searchable transcripts, episode notes, and downloadable PDF editions.';
      const metaDescription = document.querySelector('meta[name="description"]');
      const ogTitle = document.querySelector('meta[property="og:title"]');
      const ogDescription = document.querySelector('meta[property="og:description"]');
      const ogUrl = document.querySelector('meta[property="og:url"]');
      const ogImage = document.querySelector('meta[property="og:image"]');
      const ogImageAlt = document.querySelector('meta[property="og:image:alt"]');
      const twitterTitle = document.querySelector('meta[name="twitter:title"]');
      const twitterDescription = document.querySelector('meta[name="twitter:description"]');
      const twitterUrl = document.querySelector('meta[name="twitter:url"]');
      const twitterImage = document.querySelector('meta[name="twitter:image"]');
      const twitterImageAlt = document.querySelector('meta[name="twitter:image:alt"]');
      const canonical = document.querySelector('link[rel="canonical"]');

      if (metaDescription) metaDescription.setAttribute('content', defaultDescription);
      if (ogTitle) ogTitle.setAttribute('content', 'Machine Learning Street Talk | ReScript Archive');
      if (ogDescription) ogDescription.setAttribute('content', 'Search, read, and export Machine Learning Street Talk interviews through the MLST editorial transcript archive.');
      if (ogUrl) ogUrl.setAttribute('content', 'https://app.rescript.info');
      if (ogImage) ogImage.setAttribute('content', 'https://files.rescript.info/mlst-og-image.png');
      if (ogImageAlt) ogImageAlt.setAttribute('content', 'Machine Learning Street Talk ReScript archive');
      if (twitterTitle) twitterTitle.setAttribute('content', 'Machine Learning Street Talk | ReScript Archive');
      if (twitterDescription) twitterDescription.setAttribute('content', 'An editorial transcript archive for MLST podcast episodes, with searchable transcripts and downloadable PDFs.');
      if (twitterUrl) twitterUrl.setAttribute('content', 'https://app.rescript.info');
      if (twitterImage) twitterImage.setAttribute('content', 'https://files.rescript.info/mlst-og-image.png');
      if (twitterImageAlt) twitterImageAlt.setAttribute('content', 'Machine Learning Street Talk ReScript archive');
      if (canonical) canonical.setAttribute('href', 'https://app.rescript.info');
    }
  }, [sessionTitle, currentSessionId, sharePermissions, shareToken, currentSessionData?.youtubeUrl, currentSessionData?.youtube_url, youtubeLinkUrl, linkedYoutubeUrl]);

  // Update URL with current playback time
  useEffect(() => {
    // Don't update if no session or if time is still at 0
    if (!currentSessionId || !currentTime || currentTime < 1) return;
    
    // Format time as "XmYs"
    const formatTimeForUrl = (seconds: number): string => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}m${secs}s`;
    };
    
    // Debounce URL updates
    const timeoutId = setTimeout(() => {
      const timeStr = formatTimeForUrl(currentTime);
      const currentUrl = window.location.pathname + window.location.search;
      const url = new URL(window.location.href);
      
      // Update or add the time parameter
      url.searchParams.set('t', timeStr);
      
      // Only update if the URL actually changed
      const newUrl = url.pathname + url.search;
      if (currentUrl !== newUrl) {
        window.history.replaceState({}, '', newUrl);
        console.log('[URL-UPDATE] Time parameter updated:', timeStr);
      }
    }, 500); // Reduced debounce time for more responsive updates
    
    return () => clearTimeout(timeoutId);
  }, [currentTime, currentSessionId]);

  // Scroll listener for sticky header
  useEffect(() => {
    const handleScroll = () => {
      if (currentSessionId && (appState === 'completed' || appState === 'labeling')) {
        // Show sticky header when scrolled past ~400px (approximate position of session title)
        setShowStickyHeader(window.scrollY > 400);
      } else {
        setShowStickyHeader(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentSessionId, appState]);

  const getPendingSessionFilename = useCallback((resultOverride?: any) => {
    return selectedDropboxUpload?.videoFilename
      || selectedFile?.name
      || resultOverride?._videoTitle
      || (transcriptionData as any)?._videoTitle
      || 'Transcript Session';
  }, [selectedDropboxUpload?.videoFilename, selectedFile?.name, transcriptionData]);

  const buildCreateSessionRequest = useCallback((payload: {
    checksum: string;
    result: any;
    speakerLabels: SpeakerLabels;
    finalTranscript: string;
    currentTranscript: string;
    speakerSegmentsPayload: Record<string, SpeakerSegment[]>;
    distinctSpeakersPayload: number[];
  }) => {
    return {
      transcriptionChecksum: payload.checksum,
      filename: getPendingSessionFilename(payload.result),
      isYouTubeMode,
      youtubeUrl: selectedYouTubeUrl || undefined,
      transcriptionData: payload.result,
      speakerLabels: payload.speakerLabels,
      finalTranscript: payload.finalTranscript,
      currentTranscript: payload.currentTranscript,
      speakerSegments: payload.speakerSegmentsPayload,
      distinctSpeakers: payload.distinctSpeakersPayload,
      dropboxVideoUrl: selectedDropboxUpload?.dropboxVideoUrl || undefined,
      videoFilename: selectedDropboxUpload?.videoFilename || undefined,
      dropboxDrpUrl: selectedDropboxUpload?.dropboxDrpUrl || undefined,
      timelineName: selectedDropboxUpload?.timelineName || undefined,
      localVideoPath: selectedDropboxUpload?.localVideoPath || undefined,
      productionNotes: selectedDropboxUpload?.productionNotes || undefined,
      videoSourceInfo: payload.result?._videoSourceInfo || undefined,
      autoPrepareVideoSource: selectedDropboxUpload?.autoPrepareVideoSource !== false,
      showSlug: pendingShowAssignment.showSlug.trim(),
      showTitle: pendingShowAssignment.showTitle.trim(),
      familySlug: pendingFamilyAssignment.familySlug.trim() || undefined,
      familyTitle: pendingFamilyAssignment.familyTitle.trim() || undefined,
      familyVersionNumber: pendingFamilyAssignment.familySlug.trim()
        ? pendingFamilyAssignment.familyVersionNumber
        : undefined,
    };
  }, [
    getPendingSessionFilename,
    isYouTubeMode,
    pendingFamilyAssignment.familySlug,
    pendingFamilyAssignment.familyTitle,
    pendingFamilyAssignment.familyVersionNumber,
    pendingShowAssignment.showSlug,
    pendingShowAssignment.showTitle,
    selectedDropboxUpload,
    selectedYouTubeUrl,
  ]);

  const promoteSeedEpisodeIntoFamily = useCallback(async () => {
    const familySlug = pendingFamilyAssignment.familySlug.trim();
    if (!token || !pendingFamilyAssignment.seedSessionId.trim() || !familySlug) {
      return;
    }

    try {
      await sessionService.updateSession({
        sessionId: pendingFamilyAssignment.seedSessionId.trim(),
        familySlug,
        familyTitle: pendingFamilyAssignment.familyTitle.trim() || undefined,
        familyVersionNumber: 1,
      }, token);
    } catch (error) {
      console.error('[SESSION] Failed to promote seed episode into family:', error);
    }
  }, [
    pendingFamilyAssignment.familySlug,
    pendingFamilyAssignment.familyTitle,
    pendingFamilyAssignment.seedSessionId,
    token,
  ]);

  // Create or update session
  const createOrUpdateSession = useCallback(async () => {
    if (!transcriptionChecksum || !finalTranscript || !token) {
      console.log('[SESSION] Missing requirements for session creation:', {
        hasChecksum: !!transcriptionChecksum,
        hasTranscript: !!finalTranscript,
        hasToken: !!token
      });
      return;
    }

    try {
      if (!currentSessionId) {
        // Create new session
        console.log('[SESSION] Creating new session...');
        const response = await sessionService.createSession(buildCreateSessionRequest({
          checksum: transcriptionChecksum,
          result: transcriptionData,
          speakerLabels,
          finalTranscript,
          currentTranscript,
          speakerSegmentsPayload: Object.fromEntries(speakerSegments),
          distinctSpeakersPayload: distinctSpeakers,
        }), token);

        if (!response.isExisting) {
          await promoteSeedEpisodeIntoFamily();
        }
        
        setCurrentSessionId(response.sessionId);
        const newShareUrl = sessionService.generateSessionUrl(response.sessionId);
        setShareUrl(newShareUrl);
        
        // Update URL without page reload
        if (navigate) {
          navigate(`/session/${response.sessionId}`, { replace: true });
          console.log('[SESSION] URL updated to:', `/session/${response.sessionId}`);
        } else {
          console.warn('[SESSION] Navigate function not available');
        }
        
        console.log('[SESSION] Created new session:', response.sessionId);
      } else {
        // Update existing session
        await sessionService.updateSession({
          sessionId: currentSessionId,
          transcriptionData,
          speakerLabels,
          finalTranscript,
          currentTranscript,
          generatedTtmlFiles
        }, token);
        
        console.log('[SESSION] Updated session:', currentSessionId);
      }
    } catch (error) {
      console.error('[SESSION] Failed to create/update session:', error);
    }
  }, [
    transcriptionChecksum, 
    finalTranscript, 
    token, 
    currentSessionId, 
    transcriptionData,
    speakerLabels,
    currentTranscript,
    speakerSegments,
    distinctSpeakers,
    generatedTtmlFiles,
    navigate,
    buildCreateSessionRequest,
    promoteSeedEpisodeIntoFamily,
  ]);

  const handleFileSelect = useCallback((file: AudioFile) => {
    setSelectedFile(file);
    setSelectedYouTubeUrl(null);
    setSelectedDropboxUpload(null);
    setIsYouTubeMode(false);
    // Reset previous results
    setTranscriptionData(null);
    setSpeakerSegments(new Map());
    setDistinctSpeakers([]);
    setFinalTranscript('');
    setCurrentTranscript('');
    setSpeakerLabels({});
    setProcessingStatus({ status: 'idle', message: 'File selected. Click "Start Transcription" to begin.' });
  }, []);

  const handleYouTubeSelect = useCallback((url: string, cookies?: string) => {
    setSelectedYouTubeUrl(url);
    setSelectedFile(null);
    setSelectedDropboxUpload(null);
    setIsYouTubeMode(true);
    // Store cookies if provided (we'll need to pass this to the service)
    if (cookies) {
      (window as any).__youtubeCookies = cookies;
    }
    // Reset previous results
    setTranscriptionData(null);
    setSpeakerSegments(new Map());
    setDistinctSpeakers([]);
    setFinalTranscript('');
    setCurrentTranscript('');
    setSpeakerLabels({});
    setProcessingStatus({ status: 'idle', message: 'YouTube URL selected. Click "Start Transcription" to begin.' });
  }, []);

  const handleDropboxSelect = useCallback((draft: DropboxUploadDraft) => {
    setSelectedDropboxUpload(draft);
    setSelectedFile(null);
    setSelectedYouTubeUrl(null);
    setIsYouTubeMode(false);
    setDropboxVideoUrl(draft.dropboxVideoUrl);
    setVideoFilename(draft.videoFilename || null);
    setDropboxDrpUrl(draft.dropboxDrpUrl);
    setTimelineName(draft.timelineName);
    setLocalVideoPath(draft.localVideoPath);
    setProductionNotes(draft.productionNotes || null);
    setTranscriptionData(null);
    setSpeakerSegments(new Map());
    setDistinctSpeakers([]);
    setFinalTranscript('');
    setCurrentTranscript('');
    setSpeakerLabels({});
    setProcessingStatus({ status: 'idle', message: 'Dropbox video selected. Click "Start Transcription" to begin.' });
  }, []);

  const startTranscription = async () => {
    if (!selectedFile && !selectedYouTubeUrl && !selectedDropboxUpload) {
      setProcessingStatus({
        status: 'error',
        message: 'Please select an audio file, YouTube URL, or Dropbox video first.'
      });
      return;
    }

    if (!pendingShowAssignment.showSlug.trim() || !pendingShowAssignment.showTitle.trim()) {
      setProcessingStatus({
        status: 'error',
        message: 'Please choose a valid show assignment before starting transcription.'
      });
      return;
    }

    if (pendingFamilyAssignment.familySlug.trim() && !pendingFamilyAssignment.familyVersionNumber) {
      setProcessingStatus({
        status: 'error',
        message: 'Please choose a valid family version number before starting transcription.'
      });
      return;
    }

    if (
      pendingFamilyAssignment.seedSessionId.trim()
      && (!pendingFamilyAssignment.familySlug.trim() || (pendingFamilyAssignment.familyVersionNumber || 0) < 2)
    ) {
      setProcessingStatus({
        status: 'error',
        message: 'Please seed the new family from an existing session and use version 2 or higher for this upload.'
      });
      return;
    }

    try {
      setAppState('processing');

      if (selectedDropboxUpload) {
        setProcessingStatus({
          status: 'uploading',
          message: 'Extracting aligned audio from Dropbox video...',
          progress: 10,
        });
      } else if (isYouTubeMode && selectedYouTubeUrl) {
        setProcessingStatus({
          status: 'uploading',
          message: 'Extracting audio from YouTube video...',
          progress: 10,
        });
      } else {
        setProcessingStatus({
          status: 'uploading',
          message: 'Preparing audio file for transcription...',
          progress: 10,
        });
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      let result: any;
      if (selectedDropboxUpload) {
        setProcessingStatus({
          status: 'transcribing',
          message: 'Transcribing audio extracted from Dropbox video...',
          progress: 30,
        });
        result = await deepgramService.transcribeDropbox(
          selectedDropboxUpload.dropboxVideoUrl,
          settings,
          { videoFilename: selectedDropboxUpload.videoFilename || undefined },
          token!
        );
      } else if (isYouTubeMode && selectedYouTubeUrl) {
        setProcessingStatus({
          status: 'transcribing',
          message: 'Transcribing YouTube audio...',
          progress: 30,
        });
        result = await deepgramService.transcribeYoutube(selectedYouTubeUrl, settings, token!);
      } else if (selectedFile) {
        setProcessingStatus({
          status: 'transcribing',
          message: 'Checking cache and transcribing audio...',
          progress: 30,
        });
        result = await deepgramService.transcribeFile(selectedFile.file, settings, token!);
      }

      if (!result) {
        throw new Error('No transcription result was returned');
      }

      setTranscriptionData(result);
      const checksum = result._cache?.checksum || null;
      if (checksum) {
        setTranscriptionChecksum(checksum);
      }

      if (selectedDropboxUpload && result._videoSourceInfo) {
        setVideoFilename((current) => current || selectedDropboxUpload.videoFilename || null);
      }

      if (result._cache?.hit) {
        setProcessingStatus({
          status: 'completed',
          message: `Used cached transcription from ${new Date(result._cache.cachedAt).toLocaleString()}`,
          progress: 100,
        });
      }

      if (result._speakerLabels) {
        setSpeakerLabels(result._speakerLabels);
      } else {
        const defaultLabels: SpeakerLabels = {};
        TranscriptProcessor.getDistinctSpeakers(TranscriptProcessor.extractSpeakerSegments(result)).forEach((id) => {
          defaultLabels[id] = `Speaker ${id}`;
        });
        setSpeakerLabels(defaultLabels);
      }

      if (!result._cache?.hit) {
        setProcessingStatus({
          status: 'processing',
          message: selectedDropboxUpload
            ? 'Processing transcript and queueing AWS video prep...'
            : 'Processing transcript and identifying speakers...',
          progress: 70,
        });
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      const segments = TranscriptProcessor.extractSpeakerSegments(result);
      const speakers = TranscriptProcessor.getDistinctSpeakers(segments);

      setSpeakerSegments(segments);
      setDistinctSpeakers(speakers);

      const speakerMessage = `Found ${speakers.length} speaker${speakers.length !== 1 ? 's' : ''}. Ready for labeling.`;
      const cacheMessage = result._cache?.hit ? ' (from cache)' : '';

      setProcessingStatus({
        status: 'completed',
        message: speakerMessage + cacheMessage,
        progress: 100,
      });

      if (speakers.length > 0) {
        setAppState('labeling');
      } else {
        const transcript = TranscriptProcessor.generateFormattedTranscript(
          result,
          {},
          settings.timeInterval
        );

        setFinalTranscript(transcript);
        setCurrentTranscript(transcript);
        setAppState('completed');

        if (checksum && token) {
          try {
            console.log('[SESSION] Creating session (no speakers) with transcript length:', transcript.length);
            const response = await sessionService.createSession(buildCreateSessionRequest({
              checksum,
              result,
              speakerLabels: {},
              finalTranscript: transcript,
              currentTranscript: transcript,
              speakerSegmentsPayload: Object.fromEntries(segments),
              distinctSpeakersPayload: speakers,
            }), token);

            if (!response.isExisting) {
              await promoteSeedEpisodeIntoFamily();
            }

            setCurrentSessionId(response.sessionId);
            const newShareUrl = sessionService.generateSessionUrl(response.sessionId);
            setShareUrl(newShareUrl);

            if (navigate) {
              navigate(`/session/${response.sessionId}`, { replace: true });
              console.log('[SESSION] URL updated to:', `/session/${response.sessionId}`);
            }
          } catch (error) {
            console.error('[SESSION] Failed to create session (no speakers):', error);
          }
        }
      }

    } catch (error) {
      console.error('Transcription failed:', error);
      
      if (error instanceof Error && error.message.includes('Deepgram API key not configured')) {
        setProcessingStatus({
          status: 'error',
          message: 'Transcription service not configured. Please contact your administrator.'
        });
      } else {
        setProcessingStatus({
          status: 'error',
          message: error instanceof Error ? error.message : 'Transcription failed. Please try again.'
        });
      }
      
      // Reset to ready state after a delay
      setTimeout(() => {
        setAppState('ready');
        setProcessingStatus({ status: 'idle', message: 'Ready to transcribe' });
      }, 5000);
    }
  };

  const handleLabelsComplete = useCallback(async (labels: SpeakerLabels) => {
    if (!transcriptionData) return;
    setSpeakerLabels(labels);

    // Save labels to backend cache
    if (transcriptionChecksum && token) {
        try {
            await deepgramService.saveSpeakerLabels(transcriptionChecksum, labels, token);
            console.log('[CACHE] Speaker labels saved to cache.');
        } catch (error) {
            console.error('Failed to save speaker labels to cache:', error);
        }
    }

    try {
      const transcript = TranscriptProcessor.generateFormattedTranscript(
        transcriptionData,
        labels,
        settings.timeInterval
      );
      
      console.log('Generated transcript preview:', transcript.substring(0, 200));
      console.log('Transcript type:', typeof transcript);
      console.log('Transcript length:', transcript.length);
      
      setFinalTranscript(transcript);
      setCurrentTranscript(transcript);
      setAppState('completed');
      setProcessingStatus({
        status: 'completed',
        message: 'Transcript generated successfully!'
      });
      
      // Create session after transcript is generated - call directly with transcript
      if (transcriptionChecksum && token) {
        try {
          console.log('[SESSION] Creating session with transcript length:', transcript.length);
          const response = await sessionService.createSession(buildCreateSessionRequest({
            checksum: transcriptionChecksum,
            result: transcriptionData,
            speakerLabels: labels,
            finalTranscript: transcript,
            currentTranscript: transcript,
            speakerSegmentsPayload: Object.fromEntries(speakerSegments),
            distinctSpeakersPayload: distinctSpeakers,
          }), token);

          if (!response.isExisting) {
            await promoteSeedEpisodeIntoFamily();
          }
          
          setCurrentSessionId(response.sessionId);
          const newShareUrl = sessionService.generateSessionUrl(response.sessionId);
          setShareUrl(newShareUrl);
          
          // Update URL without page reload
          if (navigate) {
            navigate(`/session/${response.sessionId}`, { replace: true });
            console.log('[SESSION] URL updated to:', `/session/${response.sessionId}`);
          }
          
          if (response.isExisting) {
            console.log('[SESSION] Redirected to existing session:', response.sessionId);
            console.log('[SESSION] Message:', response.message);
          } else {
            console.log('[SESSION] Created new session:', response.sessionId);
          }
        } catch (error) {
          console.error('[SESSION] Failed to create session:', error);
        }
      }
    } catch (error) {
      console.error('Failed to generate transcript:', error);
      setProcessingStatus({
        status: 'error',
        message: 'Failed to generate final transcript. Please try again.'
      });
    }
  }, [
    transcriptionData,
    settings.timeInterval,
    transcriptionChecksum,
    token,
    createOrUpdateSession,
    speakerSegments,
    distinctSpeakers,
    buildCreateSessionRequest,
    navigate,
    promoteSeedEpisodeIntoFamily,
  ]);

  const handleStartOver = () => {
    setSelectedFile(null);
    setTranscriptionData(null);
    setSpeakerSegments(new Map());
    setDistinctSpeakers([]);
    setFinalTranscript('');
    setCurrentTranscript('');
    setSpeakerLabels({});
    setPendingFamilyAssignment({
      familySlug: '',
      familyTitle: '',
      familyVersionNumber: null,
      seedSessionId: '',
      seedSessionTitle: '',
    });
    setAppState('ready');
    setProcessingStatus({ status: 'idle', message: 'Ready to transcribe' });
    // Navigate to home URL
    if (navigate) {
      navigate('/');
    }
  };

  const handleTranscriptChange = (newTranscript: string) => {
    setCurrentTranscript(newTranscript);
  };

  const handleRestoreOriginal = () => {
    setCurrentTranscript(finalTranscript);
  };

  // Handle updating speaker labels for an existing session
  const handleUpdateSpeakerLabels = useCallback(async (newLabels: SpeakerLabels) => {
    if (!currentSessionId || !token || !transcriptionData) {
      throw new Error('Missing required data to update speaker labels');
    }

    console.log('[SPEAKERS] Updating speaker labels:', newLabels);

    // Generate new transcript with updated speaker labels
    const newTranscript = TranscriptProcessor.generateFormattedTranscript(
      transcriptionData,
      newLabels,
      settings.timeInterval
    );

    // Update local state
    setSpeakerLabels(newLabels);
    setFinalTranscript(newTranscript);
    setCurrentTranscript(newTranscript);

    // Save to backend
    await sessionService.updateSession({
      sessionId: currentSessionId,
      speakerLabels: newLabels,
      finalTranscript: newTranscript,
      currentTranscript: newTranscript
    }, token);

    console.log('[SPEAKERS] Speaker labels updated successfully');
  }, [currentSessionId, token, transcriptionData, settings.timeInterval]);

  // handleTranscriptSettingsChange removed - was only used by Timeline View tab

  const handleSessionSelect = (sessionId: string) => {
    navigate(`/session/${sessionId}`);
  };

  // Use edited transcript for AI features, fallback to final transcript
  const activeTranscript = currentTranscript || finalTranscript;

  useEffect(() => {
    setPlaybackTranscriptionData(transcriptionData);
  }, [currentSessionId, transcriptionData]);

  useEffect(() => {
    autoTitleAgentStartedRef.current = null;
  }, [currentSessionId]);

  useEffect(() => {
    if ((sessionTitle || '').trim()) {
      autoTitleAgentStartedRef.current = null;
    }
  }, [sessionTitle]);

  // Session title management functions
  const generateSessionTitle = async (options: { navigateToAgent?: boolean; automatic?: boolean } = {}) => {
    const { navigateToAgent = true, automatic = false } = options;
    if (!currentSessionId || !token || !activeTranscript) return;

    if (automatic && autoTitleAgentStartedRef.current === currentSessionId) {
      return;
    }

    setIsGeneratingTitle(true);
    try {
      const response = await startTitleGenerationSession({ sessionId: currentSessionId }, token);
      autoTitleAgentStartedRef.current = currentSessionId;
      if (navigateToAgent) {
        window.dispatchEvent(new CustomEvent('navigate-to-agent', {
          detail: { agentSessionId: response.agentSessionId },
        }));
      }
    } catch (error) {
      console.error('Failed to generate title:', error);
      if (automatic) {
        autoTitleAgentStartedRef.current = null;
      }
    } finally {
      setIsGeneratingTitle(false);
    }
  };

  const saveSessionTitle = async (newTitle: string) => {
    if (!currentSessionId || !token) return;
    
    setIsSavingTitle(true);
    try {
      await sessionService.updateTitle(currentSessionId, newTitle, token);
      setSessionTitle(newTitle);
      setIsEditingTitle(false);
    } catch (error) {
      console.error('Failed to save title:', error);
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, newTitle: string) => {
    if (e.key === 'Enter') {
      saveSessionTitle(newTitle);
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
    }
  };

  // Auto-generate title when session is completed and we have a transcript
  useEffect(() => {
    if (currentSessionId && appState === 'completed' && activeTranscript && !sessionTitle.trim()) {
      void generateSessionTitle({ navigateToAgent: false, automatic: true });
    }
  }, [currentSessionId, appState, activeTranscript, sessionTitle]);

  // Check if we're accessing via share link
  const isShareAccess = sharePermissions !== null;
  const canOpenOfflineSnapshot = !!sessionId && hasOfflineSessionSnapshot === true;
  const effectiveSessionDuration = pickPositiveDuration(
    transcriptionData?.metadata?.duration,
    currentSessionData?.duration,
    retentionData?.videoDurationSeconds
  );
  
  if (!isShareAccess && (!authStatusChecked || hasOfflineSessionSnapshot === null)) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#EEF1F3] dark:bg-[#111826]">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#FFB52B] border-t-transparent" />
      </div>
    );
  }

  if (!isAuthorized && !isShareAccess && !isLoadingShare && !canOpenOfflineSnapshot) {
    return <Login />;
  }

  if (isForbidden) {
      return <RequestAccess email={user?.email || 'your email'} />;
  }

  return (
    <div
      className="app-shell relative flex flex-col overflow-hidden bg-[#EEF1F3] text-[#232E3F] transition-colors dark:bg-[#111826] dark:text-[#F4F4F4]"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        height: viewportMetrics.height > 0 ? `${viewportMetrics.height}px` : '100dvh',
        maxHeight: viewportMetrics.height > 0 ? `${viewportMetrics.height}px` : '100dvh',
        boxSizing: 'border-box',
        overscrollBehavior: 'none',
        paddingTop: 'var(--app-mobile-top-offset)',
        paddingBottom: 'var(--app-mobile-bottom-offset)',
        ['--app-mobile-top-offset' as string]: `calc(env(safe-area-inset-top, 0px) + ${viewportMetrics.topOffset}px)`,
        ['--app-mobile-bottom-offset' as string]: `calc(env(safe-area-inset-bottom, 0px) + ${viewportMetrics.bottomOffset}px)`,
      }}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-[1] bg-[#232E3F] dark:bg-[#111826]"
        style={{ height: 'var(--app-mobile-top-offset)' }}
      />
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-20 -top-24 h-72 w-72 rounded-full bg-[#FFB52B]/[0.18] blur-3xl dark:bg-[#FFB52B]/10" />
        <div className="absolute right-[-8rem] top-10 h-[24rem] w-[24rem] rounded-full bg-[#232E3F]/[0.12] blur-3xl dark:bg-[#314158]/40" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.78),rgba(244,244,244,0.94))] dark:bg-[linear-gradient(180deg,rgba(9,14,22,0.72),rgba(17,24,38,0.96))]" />
        <div
          className="absolute inset-0 opacity-[0.05] dark:opacity-[0.08]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(35,46,63,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(35,46,63,0.12) 1px, transparent 1px)',
            backgroundSize: '34px 34px',
          }}
        />
      </div>
      <div className="relative z-10 flex h-full flex-col overflow-hidden">
      {/* Sticky Session Header */}
      <AnimatePresence>
        {showStickyHeader && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.2 }}
            className={`fixed left-0 right-0 top-0 z-50 border-b backdrop-blur-xl ${
              isDarkMode
                ? 'border-[#FFB52B]/20 bg-[#232E3F]/[0.92] text-[#F4F4F4] shadow-[0_24px_48px_-28px_rgba(10,14,20,0.72)]'
                : 'border-[#D6DCE4] bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(244,244,244,0.97))] text-[#232E3F] shadow-[0_20px_40px_-30px_rgba(35,46,63,0.24)]'
            }`}
            style={{ top: 'var(--app-mobile-top-offset)' }}
          >
            <div className="max-w-4xl mx-auto px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="rounded-2xl border border-[#FFB52B]/25 bg-[#FFB52B]/[0.12] p-2 shadow-[0_16px_30px_-24px_rgba(255,181,43,0.95)]">
                    <Mic size={20} className="text-brand-yellow" />
                  </div>
                  <div>
                    <p className={`font-headlines text-[10px] font-semibold uppercase tracking-[0.28em] ${
                      isDarkMode ? 'text-[#FFB52B]/80' : 'text-[#A36A00]'
                    }`}>
                      Active Session
                    </p>
                    <h2 className={`max-w-96 truncate font-headlines text-lg font-bold ${
                      isDarkMode ? 'text-[#F4F4F4]' : 'text-[#232E3F]'
                    }`}>
                      {sessionTitle || 'Untitled Session'}
                    </h2>
                    <p className={`font-body text-xs ${isDarkMode ? 'text-[#D5D9E0]' : 'text-[#5B687C]'}`}>
                      {selectedFile?.name || (transcriptionData as any)?._videoTitle || 'Session'} • 
                      {currentSessionId?.substring(0, 8)}...
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className={`rounded-full border p-2 transition-colors ${
                      isDarkMode
                        ? 'border-white/10 bg-white/[0.05] text-white/80 hover:border-[#FFB52B]/40 hover:bg-[#FFB52B] hover:text-[#232E3F]'
                        : 'border-[#D6DCE4] bg-white/[0.92] text-[#4A586E] hover:border-[#FFB52B]/45 hover:bg-[#FFB52B] hover:text-[#232E3F]'
                    }`}
                    title="Scroll to top"
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className={`rounded-full border p-2 transition-colors ${
                      isDarkMode
                        ? 'border-white/10 bg-white/[0.05] text-white/80 hover:border-[#FFB52B]/40 hover:bg-[#FFB52B] hover:text-[#232E3F]'
                        : 'border-[#D6DCE4] bg-white/[0.92] text-[#4A586E] hover:border-[#FFB52B]/45 hover:bg-[#FFB52B] hover:text-[#232E3F]'
                    }`}
                    title="Return to Home"
                  >
                    <Home size={16} />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fixed header - Hidden in fullscreen */}
      {!isTranscriptFullscreen && (
        <AppHeader
          isDarkMode={isDarkMode}
          toggleDarkMode={toggleDarkMode}
          userName={user?.name}
          userEmail={user?.email}
          userPicture={user?.picture}
          onLogout={() => { void logout(); }}
          onHome={() => navigate('/')}
          sessionId={currentSessionId}
          sessionTitle={sessionTitle}
          sessionShowSlug={currentSessionData?.showSlug || null}
          sessionShowTitle={currentSessionData?.showTitle || null}
          sessionFamily={currentSessionData?.family || null}
          token={token || undefined}
          onTitleUpdate={(newTitle) => setSessionTitle(newTitle)}
          onSessionRefresh={reloadSessionData}
          activeTabId={activeTabId}
          onTabChange={setActiveTabId}
          availableTabIds={availableTabIds}
          selectedTtmlName={currentSessionData?.selectedTtmlId ?
            generatedTtmlFiles.find(f => ((f as any).filename || f.name) === currentSessionData.selectedTtmlId)?.name :
            undefined}
          youtubeUrl={youtubeLinkUrl}
          onYoutubeUrlChange={handleYoutubeUrlChange}
          videoUrl={linkedYoutubeUrl}
          videoSource={linkedVideoSource}
          onVideoUrlChange={handleVideoUrlChange}
          onRefreshVideoSource={() => currentSessionId ? refreshCurrentVideoSourceStatus(currentSessionId) : undefined}
          onToggleYoutubePlayer={() => toggleVideoPanelVisibility()}
          isYoutubePlayerVisible={isYoutubePlayerVisible}
          videoStats={retentionData?.videoStats}
          videoDurationSeconds={retentionData?.videoDurationSeconds}
          fetchedAt={retentionData?.fetchedAt}
          onRetentionDataUpdate={(data) => setRetentionData(data)}
          speakerIds={distinctSpeakers}
          speakerSegments={speakerSegments}
          speakerLabels={speakerLabels}
          onSaveSpeakerLabels={handleUpdateSpeakerLabels}
          transcriptionChecksum={transcriptionChecksum || currentSessionData?.transcriptionChecksum}
          isYouTubeMode={isYouTubeMode || currentSessionData?.isYouTubeMode}
          youtubeTitle={currentSessionData?.youtubeTitle}
          youtubeTitleUpdatedAt={currentSessionData?.youtubeTitleUpdatedAt}
          youtubeTitleSync={currentSessionData?.youtubeTitleSync}
          dropboxVideoUrl={dropboxVideoUrl}
          videoFilename={videoFilename}
          dropboxDrpUrl={dropboxDrpUrl}
          timelineName={timelineName}
          localVideoPath={localVideoPath}
          productionNotes={productionNotes}
          videoFileHash={currentSessionData?.videoFileHash}
          videoFileSize={currentSessionData?.videoFileSize}
          videoFrameRate={currentSessionData?.videoFrameRate}
          videoDuration={currentSessionData?.videoDuration}
          audioDuration={effectiveSessionDuration ?? currentSessionData?.duration}
          videoSourceStatus={currentSessionData?.videoSourceStatus}
          customThumbnailUrl={currentSessionData?.customThumbnailUrl}
          customThumbnailTimeSeconds={currentSessionData?.customThumbnailTimeSeconds}
          publishedToHomepage={publishedToHomepage}
          onPublishChange={(published) => setPublishedToHomepage(published)}
          isOfflineAvailable={isOfflineAvailable}
          isOfflineMode={isOfflineSessionMode}
          isOfflineActionPending={isOfflineActionPending}
          offlineActionStatusLabel={offlineActionStatusLabel || undefined}
          onToggleOfflineAvailability={
            currentSessionId && (shareToken || isOfflineAvailable || isOfflineSessionMode)
              ? handleToggleOfflineAvailability
              : undefined
          }
          onCopyOfflineDiagnostics={currentSessionId ? handleCopyOfflineDiagnostics : undefined}
          onNavigateToFamilyMember={(sessionId) => {
            navigate(`/session/${sessionId}`);
          }}
          onProductionMetadataUpdate={(data) => {
            if (data.dropboxVideoUrl !== undefined) setDropboxVideoUrl(data.dropboxVideoUrl);
            if (data.videoFilename !== undefined) setVideoFilename(data.videoFilename);
            if (data.dropboxDrpUrl !== undefined) setDropboxDrpUrl(data.dropboxDrpUrl);
            if (data.timelineName !== undefined) setTimelineName(data.timelineName);
            if (data.localVideoPath !== undefined) setLocalVideoPath(data.localVideoPath);
            if (data.productionNotes !== undefined) setProductionNotes(data.productionNotes);
          }}
          onVideoSourceStatusUpdate={(status) => {
            const resolvedVideoSelection = resolveCanonicalVideoSelection(currentSessionData, status);
            setCurrentSessionData((prev: any) => prev ? ({
              ...prev,
              videoSourceStatus: status,
              videoUrl: resolveCanonicalVideoSelection(prev, status).url,
              videoSource: resolveCanonicalVideoSelection(prev, status).source,
              videoFileHash: status.videoMetadata.hash || prev.videoFileHash,
              videoFileSize: status.videoMetadata.fileSize ?? prev.videoFileSize,
              videoFrameRate: status.videoMetadata.frameRate ?? prev.videoFrameRate,
              videoDuration: status.videoMetadata.duration ?? prev.videoDuration,
            }) : prev);

            if (resolvedVideoSelection.url && resolvedVideoSelection.source) {
              setLinkedYoutubeUrl(resolvedVideoSelection.url);
              setLinkedVideoSource(resolvedVideoSelection.source);
              setYoutubeLinkUrl(resolvedVideoSelection.youtubeUrl);
              if (isYoutubePlayerVisible) {
                setPlayerMode(resolvePlayerModeForVideoSource(resolvedVideoSelection.source));
              }
            } else {
              setLinkedYoutubeUrl(null);
              setLinkedVideoSource(null);
              setYoutubeLinkUrl(resolvedVideoSelection.youtubeUrl);
              setIsYoutubePlayerVisible(false);
              setPlayerMode('audio');
            }
          }}
        />
      )}

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Tab Layout for completed state */}
        {appState === 'completed' && hasSelectedInput && finalTranscript ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
            {/* Video Player - single instance, responsive layout */}
            {shouldShowVideoPlayer && effectiveVideoUrl && (
              <>
                {/* On mobile: full width at top. On desktop: side panel */}
                <div
                  data-session-video-panel="true"
                  className="relative flex-shrink-0 w-full md:w-auto"
                  style={{ width: typeof window !== 'undefined' && window.innerWidth >= 768 ? (isYoutubePlayerFloating ? 0 : youtubePlayerWidth) : '100%' }}
                >
                  <VideoPlayer
                    videoUrl={effectiveVideoUrl}
                    videoSource={effectiveVideoSource || 'youtube'}
                    useExternalAudioSync={effectiveVideoSource === 'file'}
                    sessionId={currentSessionId}
                    selectedTtmlChecksum={selectedTtmlChecksum}
                    selectedTranscriptChecksum={selectedTranscriptChecksum}
                    totalDuration={duration}
                    token={token || undefined}
                    speakerSegments={speakerSegments}
                    speakerLabels={speakerLabels}
                    isVisible={isYoutubePlayerVisible}
                    onClose={() => toggleVideoPanelVisibility(false)}
                    onTimeUpdate={effectiveVideoSource === 'file' ? ignoreHostedVideoPlaybackSignal : setYouTubeTime}
                    onDurationChange={effectiveVideoSource === 'file' ? (() => {}) : setYouTubeDuration}
                    onPlayStateChange={effectiveVideoSource === 'file' ? ignoreHostedVideoPlaybackSignal : setYouTubePlaying}
                    onReady={() => setPlayerMode(resolvePlayerModeForVideoSource(effectiveVideoSource || null))}
                    currentTime={currentTime}
                    mediaSeekRequestId={mediaSeekRequestId}
                    mediaSeekRequestTime={mediaSeekRequestTime}
                    isPlaying={isPlaying}
                    volume={volume}
                    playbackRate={playbackRate}
                    isDarkMode={isDarkMode}
                    isMinimized={false}
                    onFloatingChange={perfSettings.isMobile ? undefined : setIsYoutubePlayerFloating}
                    isMobile={perfSettings.isMobile}
                    mobileTopicTitle={perfSettings.isMobile && activeTabId === 'readable' ? currentTranscriptTopicTitle : null}
                  />
                </div>
                {/* Draggable Resizer */}
                {!isYoutubePlayerFloating && (
                  <div
                    className={`group hidden w-1.5 flex-shrink-0 cursor-col-resize items-center justify-center transition-colors hover:bg-[#FFB52B]/[0.18] md:flex ${
                      isResizingYoutubePanel ? 'bg-[#FFB52B]/26' : ''
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setIsResizingYoutubePanel(true);
                    }}
                  >
                    <div className={`h-8 w-0.5 rounded-full transition-colors ${
                      isDarkMode
                        ? 'bg-[#556176] group-hover:bg-[#FFB52B]'
                        : 'bg-[#9AA6B7] group-hover:bg-[#FFB52B]'
                    } ${isResizingYoutubePanel ? 'bg-[#FFB52B]' : ''}`} />
                  </div>
                )}
              </>
            )}
            {!shouldShowVideoPlayer && effectiveVideoUrl && (
              <>
                <div className={`hidden w-14 flex-shrink-0 border-r md:flex ${isDarkMode ? 'border-[#39465A] bg-[#182130]/[0.92]' : 'border-[#D4DAE2] bg-[#F8F8F7]/[0.92]'}`}>
                  <button
                    type="button"
                    onClick={() => toggleVideoPanelVisibility(true)}
                    className={`flex h-full w-full flex-col items-center justify-center gap-4 transition-colors ${
                      isDarkMode
                        ? 'text-[#D8DDE5] hover:bg-[#232E3F]'
                        : 'text-[#4A586E] hover:bg-[#FFFFFF]'
                    }`}
                    title="Open video panel"
                  >
                    {effectiveVideoSource === 'youtube' ? <Youtube className="h-5 w-5 text-red-500" /> : <Video className="h-5 w-5" />}
                    <span className="rotate-180 font-headlines text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ writingMode: 'vertical-rl' }}>
                      Video
                    </span>
                  </button>
                </div>
              </>
            )}
            {/* Tab Layout */}
            <div className="flex-1 flex flex-col overflow-hidden">
          <TabLayout
            isDarkMode={isDarkMode}
            activeTab={activeTabId}
            onTabChange={setActiveTabId}
            hideNavigation={isTranscriptFullscreen}
            tabs={(() => {
              const allTabs: Array<{
                id: TabId;
                title: string;
                icon: React.ReactNode;
                component: React.ComponentType<any>;
                props: any;
              }> = [
              {
                id: 'ttml' as TabId,
                title: 'Ranking and Metadata',
                icon: <BarChart3 size={18} />,
                component: TTMLRankingEnhanced,
                props: {
                  transcript: activeTranscript,
                  filename: selectedFile?.name || (transcriptionData as any)?._videoTitle || 'YouTube Video',
                  transcriptionData: transcriptionData || undefined,
                  transcriptionChecksum: transcriptionChecksum || undefined,
                  sessionId: currentSessionId || undefined,
                  currentSessionData: currentSessionData,
                  onTtmlGenerated: updateGeneratedTtmlFiles,
                  speakerLabels: speakerLabels,
                  audioFile: selectedFile?.file || undefined,
                  onTtmlPreview: (ttmlId: string | null) => {
                    setPreviewTtmlId(ttmlId);
                    // Don't update session data here - only preview
                  },
                  onTtmlSelected: (ttmlId: string) => {
                    // Update session data when TTML is actually selected
                    if (currentSessionData) {
                      setCurrentSessionData({
                        ...currentSessionData,
                        selectedTtmlId: ttmlId
                      });
                    }
                  }
                }
              },
              {
                id: 'chat' as TabId,
                title: 'Chat with Transcript',
                icon: <MessageSquare size={18} />,
                component: ChatInterface,
                props: {
                  transcript: activeTranscript,
                  model: selectedAIModel,
                  onModelChange: setSelectedAIModel,
                  supportedModels: supportedModels,
                  currentTtmlFiles: (() => {
                    // If a specific TTML is selected, use that one
                    if (currentSessionData?.selectedTtmlId) {
                      const selected = generatedTtmlFiles.filter(f =>
                        (f as any).checksum === currentSessionData.selectedTtmlId ||
                        (f as any).filename === currentSessionData.selectedTtmlId ||
                        f.name === currentSessionData.selectedTtmlId
                      );
                      return selected;
                    }
                    // Otherwise, if there are TTML files, use the first one as default
                    if (generatedTtmlFiles.length > 0) {
                      return [generatedTtmlFiles[0]];
                    }
                    // No TTML files available
                    return [];
                  })(),
                  sessionId: currentSessionId,
                  transcriptionData: transcriptionData,
                  transcriptionChecksum: transcriptionChecksum || undefined,
                  speakerLabels: speakerLabels,
                  shareToken: shareToken || undefined,
                  isPublicView: !!shareToken,
                  narrativeData: narrativeData,
                  youtubeRetentionData: (() => {
                    console.log('[TAB-CONFIG] Setting youtubeRetentionData in ChatInterface props:', retentionData ? { videoId: retentionData.videoId, hasStats: !!retentionData.videoStats } : null);
                    return retentionData;
                  })()
                }
              },
              {
                id: 'agent' as TabId,
                title: 'Agent',
                icon: <Sparkles size={18} />,
                component: AgentChat,
                props: {
                  sessionId: currentSessionId,
                  isPublicView: !!shareToken,
                  shareToken: shareToken || undefined,
                  token: token || undefined,
                  preferredSessionId: pendingAgentSessionId,
                  onPreferredSessionHandled: () => setPendingAgentSessionId(null),
                }
              },
              {
                id: 'readable' as TabId,
                title: 'Transcript',
                icon: <BookOpen size={18} />,
                component: ReadableTranscriptTab,
                props: {
                  ttmlFiles: generatedTtmlFiles,
                  sessionId: currentSessionId,
                  transcriptionChecksum: transcriptionChecksum || undefined,
                  selectedTranscriptChecksum: selectedTranscriptChecksum || undefined,
                  transcript: activeTranscript,
                  filename: selectedFile?.name || (transcriptionData as any)?._videoTitle || 'YouTube Video',
                  transcriptionData: transcriptionData || undefined,
                  speakerLabels: speakerLabels,
                  speakerSegments: speakerSegments,
                  duration: effectiveSessionDuration,
                  youtubeUrl: isOfflineSessionMode ? null : youtubeLinkUrl,
                  speakerCount: distinctSpeakers.length,
                  comments: currentSessionData?._comments || [],
                  onTranscriptChange: handleTranscriptChange,
                  onRestoreOriginal: handleRestoreOriginal,
                  selectedTtmlId: currentSessionData?.selectedTtmlId,
                  onFullscreenChange: setIsTranscriptFullscreen,
                  onSearchMatchesChange: setTranscriptSearchMatches,
                  onActiveTranscriptionDataChange: (data: DeepgramResponse | undefined) => {
                    setPlaybackTranscriptionData(data || transcriptionData);
                  },
                  onCurrentTopicChange: setCurrentTranscriptTopicTitle,
                  showVideoPanel: isOfflineSessionMode ? false : shouldShowVideoPlayer,
                  gapAnalysisData: gapAnalysisData,
                  onFilteredGapClustersChange: (clusters: any[], ncGaps?: any[]) => {
                    setFilteredGapClusters(clusters);
                    setNonClusteredGaps(ncGaps || null);
                  }
                }
              },
              {
                id: 'narrative' as TabId,
                title: 'Topics Map',
                icon: <GitBranch size={18} />,
                component: NarrativeTimeline,
                props: {
                  sessionId: currentSessionId || '',
                  transcript: activeTranscript,
                  ttml: currentSessionData?.selectedTtmlId && generatedTtmlFiles.length > 0 ?
                    generatedTtmlFiles.find(f =>
                      (f as any).checksum === currentSessionData.selectedTtmlId ||
                      (f as any).filename === currentSessionData.selectedTtmlId ||
                      f.name === currentSessionData.selectedTtmlId
                    )?.content || '' : '',
                  maxDuration: (transcriptionData?.metadata?.duration || 0) / 60, // Convert to minutes
                  title: 'Narrative Timeline',
                  shareToken: shareToken || undefined,
                  youtubeUrl: linkedYoutubeUrl
                }
              },
              {
                id: 'description' as TabId,
                title: 'Description',
                icon: <FileEdit size={18} />,
                component: DescriptionTab,
                props: {
                  sessionId: currentSessionId || '',
                  youtubeDescription: currentSessionData?.youtubeDescription || '',
                  ttmlContent: currentSessionData?.selectedTtmlId && generatedTtmlFiles.length > 0 ?
                    generatedTtmlFiles.find(f =>
                      (f as any).checksum === currentSessionData.selectedTtmlId ||
                      (f as any).filename === currentSessionData.selectedTtmlId ||
                      f.name === currentSessionData.selectedTtmlId
                    )?.content || '' : '',
                  sessionTitle: sessionTitle,
                  linkedYoutubeUrl: isOfflineSessionMode ? null : linkedYoutubeUrl,
                  transcript: finalTranscript || currentTranscript || '',
                  onDescriptionChange: (desc: string) => {
                    // Update session data when description changes
                    if (currentSessionData) {
                      setCurrentSessionData({
                        ...currentSessionData,
                        youtubeDescription: desc
                      });
                    }
                  },
                  onSeek: seekTo,
                  forceReadOnly: isOfflineSessionMode,
                }
              },
              {
                id: 'context' as TabId,
                title: 'Context',
                icon: <Paperclip size={18} />,
                component: ContextFilesTab,
                props: {
                  sessionId: currentSessionId || '',
                  isPublicView: !!sharePermissions,
                  shareToken: shareToken,
                  sharePermissions: sharePermissions,
                  isDarkMode: isDarkMode
                }
              },
              {
                id: 'questions' as TabId,
                title: 'Questions',
                icon: <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full border border-current text-[10px] font-bold leading-none">Q</span>,
                component: QuestionsTab,
                props: {
                  sessionId: currentSessionId || '',
                  isDarkMode: isDarkMode,
                  onSeek: seekTo,
                }
              }
            ];

            // Filter tabs based on share permissions if viewing through share link
            if (isOfflineSessionMode) {
              return allTabs.filter(tab => tab.id === 'readable' || tab.id === 'description');
            }

            if (sharePermissions) {
              return allTabs.filter(tab => {
                switch (tab.id) {
                  case 'ttml':
                    return false; // Ranking & Metadata is never available on public shares
                  case 'agent':
                    return false; // Agent is never available on public shares
                  case 'chat':
                    return sharePermissions.chatMode !== 'DISABLED';
                  case 'readable':
                    return sharePermissions.transcriptsEnabled;
                  case 'narrative':
                    return sharePermissions.rankingEnabled && shareHasNarrativeData;
                  case 'description':
                    return sharePermissions.descriptionEnabled !== false; // Show if enabled in share link
                  case 'context':
                    return sharePermissions.contextFilesEnabled === true && shareHasContextFiles;
                  case 'questions':
                    return sharePermissions.questionsEnabled === true;
                  default:
                    return false;
                }
              });
            }

            // Filter agent tab for non-authenticated users (belt-and-suspenders; API also blocks)
            if (!token) {
              return allTabs.filter(tab => tab.id !== 'agent');
            }

            // Return all tabs for authenticated users
            return allTabs;
          })()}
          />
            </div>
          </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-5xl px-4 pb-20">
            {/* Session Title */}
            {(appState === 'completed' || appState === 'labeling') && currentSessionId && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 space-y-3"
            >
              <div className="rounded-[28px] border border-[#D6DCE4] bg-[#F8F8F7]/[0.92] p-6 shadow-[0_28px_60px_-36px_rgba(35,46,63,0.42)] backdrop-blur dark:border-[#344052] dark:bg-[#1B2432]/[0.92]">
                <div className="flex items-center justify-between space-x-4">
                  <div className="flex-1">
                    {isEditingTitle ? (
                      <input
                        type="text"
                        defaultValue={sessionTitle}
                        onKeyPress={(e) => handleTitleKeyPress(e, (e.target as HTMLInputElement).value)}
                        onBlur={(e) => saveSessionTitle(e.target.value)}
                        className="w-full border-b-2 border-[#FFB52B] bg-transparent font-headlines text-xl font-bold text-[#232E3F] focus:outline-none dark:text-[#F4F4F4]"
                        placeholder="Enter session title..."
                        autoFocus
                      />
                    ) : (
                      <h2 className="font-headlines text-xl font-bold text-[#232E3F] dark:text-[#F4F4F4]">
                        {sessionTitle || 'Untitled Session'}
                      </h2>
                    )}
                    <p className="mt-1 font-body text-sm text-[#566377] dark:text-[#C6CDD8]">
                      {selectedFile?.name || (transcriptionData as any)?._videoTitle || 'Session'} • 
                      {currentSessionId?.substring(0, 8)}...
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {!isEditingTitle && !sharePermissions && (
                      <>
                        <button
                          onClick={() => { void generateSessionTitle(); }}
                          disabled={isGeneratingTitle || !activeTranscript}
                          className="flex items-center space-x-2 rounded-2xl bg-[#232E3F] px-4 py-2.5 font-headlines text-sm font-semibold text-[#F4F4F4] shadow-[0_18px_32px_-24px_rgba(35,46,63,0.78)] transition-all hover:bg-[#2F3B4E] hover:shadow-[0_22px_36px_-24px_rgba(35,46,63,0.88)] disabled:cursor-not-allowed disabled:opacity-50"
                          title="Start title generation agent"
                        >
                          {isGeneratingTitle ? (
                            <>
                              <Loader2 className="animate-spin" size={16} />
                              <span>Generating...</span>
                            </>
                          ) : (
                            <>
                              <Sparkles size={16} />
                              <span>AI Title</span>
                            </>
                          )}
                        </button>
                        
                        <button
                          onClick={() => setIsEditingTitle(true)}
                          className="rounded-2xl border border-[#D6DCE4] p-2 text-[#566377] transition-colors hover:border-[#FFB52B]/60 hover:bg-[#FFF8EA] hover:text-[#232E3F] dark:border-[#3A4658] dark:text-[#C6CDD8] dark:hover:bg-[#2A3446] dark:hover:text-[#F4F4F4]"
                          title="Edit title"
                        >
                          <Edit3 size={16} />
                        </button>
                      </>
                    )}
                    
                    {isSavingTitle && (
                      <div className="flex items-center space-x-2 text-[#C98A00] dark:text-[#FFB52B]">
                        <Loader2 className="animate-spin" size={16} />
                        <span className="text-sm">Saving...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Session Loading */}
          {isLoadingSession && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="rounded-[28px] border border-[#D6DCE4] bg-[#F8F8F7]/[0.92] p-8 shadow-[0_28px_60px_-36px_rgba(35,46,63,0.42)] backdrop-blur dark:border-[#344052] dark:bg-[#1B2432]/[0.92]">
                <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-[#FFB52B] border-t-transparent"></div>
                <h3 className="mb-2 font-headlines text-lg font-bold text-[#232E3F] dark:text-[#F4F4F4]">Loading Session</h3>
                <p className="font-body text-[#566377] dark:text-[#C6CDD8]">Restoring your session data...</p>
              </div>
            </motion.div>
          )}

          {/* Main Content */}
          {!isLoadingSession && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
          {/* File Upload */}
          {(appState === 'ready' || appState === 'processing') && (
            <FileUpload
              onFileSelect={handleFileSelect}
              onYouTubeSelect={handleYouTubeSelect}
              onDropboxSelect={handleDropboxSelect}
              showAssignment={pendingShowAssignment}
              onShowAssignmentChange={setPendingShowAssignment}
              familyAssignment={pendingFamilyAssignment}
              onFamilyAssignmentChange={setPendingFamilyAssignment}
              isProcessing={appState === 'processing'}
            />
          )}

          {/* Session Browser - Only show on ready state and when not processing */}
          {appState === 'ready' && !hasSelectedInput && (
            <SessionBrowser
              onSessionSelect={handleSessionSelect}
              className="mb-6"
            />
          )}

          {/* Start Transcription Button */}
          {appState === 'ready' && hasSelectedInput && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <button
                onClick={startTranscription}
                className="rounded-full bg-[#232E3F] px-8 py-3 font-headlines text-lg font-semibold text-[#F4F4F4] shadow-[0_24px_42px_-24px_rgba(35,46,63,0.7)] transition-all hover:-translate-y-0.5 hover:bg-[#2F3B4E] hover:shadow-[0_28px_48px_-24px_rgba(35,46,63,0.82)]"
              >
                Start Transcription
              </button>
            </motion.div>
          )}

          {/* Processing Status */}
          {appState === 'processing' && (
            <ProcessingStatus status={processingStatus} />
          )}

          {/* Orphan Session Recovery */}
          {currentSessionData?.audioStatus?.isOrphan && !isYouTubeMode && (
            <OrphanSessionRecovery
              sessionId={currentSessionData.sessionId}
              filename={currentSessionData.filename}
              transcriptionChecksum={currentSessionData.transcriptionChecksum}
              onRecoverySuccess={reloadSessionData}
              className="mb-6"
            />
          )}

          {/* Speaker Labeling */}
          {appState === 'labeling' && hasSelectedInput && (
            <SpeakerLabeling
              sessionId={currentSessionId || undefined}
              speakers={distinctSpeakers}
              speakerSegments={speakerSegments}
              audioFile={selectedFile}
              onLabelsComplete={handleLabelsComplete}
              initialLabels={speakerLabels}
              isYouTubeMode={isYouTubeMode}
              transcriptionChecksum={transcriptionChecksum || undefined}
            />
          )}

            {/* Footer */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="pt-8 text-center font-headlines text-[11px] font-semibold uppercase tracking-[0.24em] text-[#6B788B] dark:text-[#8D99AA]"
            >
              © MLST, 2025
            </motion.div>
          </motion.div>
        )}
            </div>
          </div>
        )}
        {/* Central Audio Player - Only show when session is loaded */}
        {(() => {
          console.log('[APP] CentralAudioPlayer condition:', {
            currentSessionId: currentSessionId?.substring(0, 8),
            appState,
            shouldRender: !!(currentSessionId && (appState === 'completed' || appState === 'labeling'))
          });
          return null;
        })()}
        {currentSessionId && (appState === 'completed' || appState === 'labeling') && (
          <CentralAudioPlayer
            sessionId={currentSessionId}
            speakerSegments={speakerSegments}
            speakerLabels={speakerLabels}
            transcriptionData={playbackTranscriptionData || transcriptionData}
            ttmlFiles={generatedTtmlFiles}
            duration={effectiveSessionDuration}
            selectedTtmlId={previewTtmlId || currentSessionData?.selectedTtmlId}
            shareToken={shareToken || undefined}
            isPublicView={!!shareToken}
            retentionData={retentionData}
            youtubeUrl={linkedYoutubeUrl}
            initialComments={currentSessionData?._comments}
            fromCdn={currentSessionData?._fromCdn}
            onTtmlRevisionCreated={handleTtmlRevisionCreated}
            searchMatches={transcriptSearchMatches}
            gapClusters={filteredGapClusters || gapAnalysisData?.clusters}
            nonClusteredGaps={nonClusteredGaps || undefined}
          />
        )}
      </div>
      {!shouldShowVideoPlayer && effectiveVideoUrl && perfSettings.isMobile && activeTabId === 'readable' && (
        <button
          type="button"
          onClick={() => toggleVideoPanelVisibility(true)}
          className={`fixed right-4 z-40 inline-flex items-center gap-2 rounded-full border px-4 py-3 text-sm font-headlines font-semibold shadow-lg backdrop-blur ${
            isDarkMode
              ? 'border-[#445166] bg-[#232E3F]/95 text-[#F4F4F4] hover:border-[#FFB52B]/60 hover:bg-[#2D3A4F]'
              : 'border-[#D4DAE2] bg-white/95 text-[#232E3F] hover:border-[#FFB52B]/60 hover:bg-[#FFF8EA]'
          }`}
          style={{ bottom: 'calc(var(--app-mobile-bottom-offset) + 5rem)' }}
          title="Open video panel"
        >
          {effectiveVideoSource === 'youtube' ? (
            <Youtube className="h-4 w-4 text-red-500" />
          ) : (
            <Video className="h-4 w-4 text-brand-yellow" />
          )}
          <span>Video</span>
        </button>
      )}
      <AnimatePresence>
        {offlineToast && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            className="pointer-events-none fixed bottom-6 right-6 z-[220] max-w-sm"
          >
            <div
              className={`rounded-2xl border px-4 py-3 text-sm font-medium shadow-[0_22px_50px_-28px_rgba(15,23,42,0.75)] backdrop-blur ${
                offlineToast.tone === 'error'
                  ? 'border-red-200 bg-red-50/95 text-red-700 dark:border-red-900/60 dark:bg-red-950/85 dark:text-red-100'
                  : 'border-[#FFB52B]/35 bg-white/95 text-[#232E3F] dark:border-[#FFB52B]/30 dark:bg-[#1B2432]/95 dark:text-[#F4F4F4]'
              }`}
            >
              {offlineToast.message}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
}

// Home page component
function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { isDarkMode } = useDarkMode();

  // Show VideoGallery for unauthenticated users, TranscriberApp for authenticated users
  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-700'}`}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FFB52B]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={isDarkMode ? 'dark' : ''}>
        <VideoGallery />
      </div>
    );
  }

  return <TranscriberApp navigate={navigate} />;
}

// Public share redirect component
function PublicShareRedirect() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const forwardedQuery = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const forwarded = new URLSearchParams();
    const timeValue = params.get('t');
    if (timeValue) {
      forwarded.set('t', timeValue);
    }
    const query = forwarded.toString();
    return query ? `&${query}` : '';
  }, [location.search]);
  
  useEffect(() => {
    const fetchSessionId = async () => {
      const tryOpenOfflineShare = async (): Promise<boolean> => {
        if (!shareToken) {
          return false;
        }

        const offlineSessionId = await getOfflineSessionIdForShare(shareToken);
        if (!offlineSessionId) {
          return false;
        }

        navigate(`/session/${offlineSessionId}?share=${shareToken}${forwardedQuery}`, { replace: true });
        return true;
      };

      try {
        if (!navigator.onLine && await tryOpenOfflineShare()) {
          return;
        }

        const response = await fetchWithTimeout(
          `${API_BASE_URL}/api/public/share/${shareToken}`,
          { cache: 'no-store', credentials: 'omit' },
          PUBLIC_SHARE_LOAD_TIMEOUT_MS,
          'Timed out while loading the shared session'
        );
        if (response.ok) {
          const data = await response.json();
          setSessionId(data.session.session_id);
        } else {
          setError('Invalid share link');
        }
      } catch (err) {
        if (await tryOpenOfflineShare()) {
          return;
        }

        setError('Failed to load share link');
      }
    };
    
    if (shareToken) {
      fetchSessionId();
    }
  }, [forwardedQuery, navigate, shareToken]);
  
  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2 text-red-600">{error}</h1>
          <p className="text-gray-600">The shared session you're looking for is not available.</p>
        </div>
      </div>
    );
  }
  
  if (sessionId) {
    // Use window.location instead of navigate to ensure a full page load
    window.location.href = `/session/${sessionId}?share=${shareToken}${forwardedQuery}`;
    return null;
  }
  
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFB52B]" />
    </div>
  );
}

// Settings page component
function SettingsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { isDarkMode } = useDarkMode();

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-700'}`}>
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FFB52B]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <Login />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Settings
            </h1>
            <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Organization controls, billing, and YouTube authentication.
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isDarkMode
                ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Back to Home
          </button>
        </div>

        <PermissionSettingsPage />
      </div>
    </div>
  );
}

function RuntimeThemeColorSync() {
  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const themeColor = isDarkMode ? DARK_THEME_COLOR : LIGHT_THEME_COLOR;
    const statusBarStyle = isDarkMode ? 'black' : 'default';
    const themeMetaTags = Array.from(document.querySelectorAll('meta[name="theme-color"]'));
    const statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');

    if (themeMetaTags.length === 0) {
      const themeMeta = document.createElement('meta');
      themeMeta.setAttribute('name', 'theme-color');
      themeMeta.setAttribute('content', themeColor);
      document.head.appendChild(themeMeta);
    } else {
      themeMetaTags.forEach((metaTag) => {
        metaTag.setAttribute('content', themeColor);
      });
    }

    if (statusBarMeta) {
      statusBarMeta.setAttribute('content', statusBarStyle);
    }

    document.documentElement.style.colorScheme = isDarkMode ? 'dark' : 'light';
    document.documentElement.style.backgroundColor = themeColor;
    document.body.style.backgroundColor = themeColor;
  }, [isDarkMode]);

  return null;
}

// Main App component with routing
function App() {
  console.log('[APP] Current location:', window.location.pathname + window.location.search);
  const [creditLimitNotice, setCreditLimitNotice] = useState<CreditLimitNotice | null>(null);

  useEffect(() => {
    const handleCreditLimitEvent = (event: Event) => {
      const customEvent = event as CustomEvent<CreditLimitNotice>;
      if (customEvent.detail?.message) {
        setCreditLimitNotice(customEvent.detail);
      }
    };

    window.addEventListener(CREDIT_LIMIT_EVENT, handleCreditLimitEvent as EventListener);

    if (!window.__rescriptCreditFetchPatched) {
      const originalFetch = window.fetch.bind(window);
      window.fetch = (async (...args: Parameters<typeof fetch>) => {
        const response = await originalFetch(...args);
        if (response.status === 402) {
          try {
            const payload = await response.clone().json();
            if (payload?.code === 'CREDIT_LIMIT_REACHED') {
              const input = args[0];
              const sourceUrl =
                typeof input === 'string'
                  ? input
                  : input instanceof Request
                    ? input.url
                    : null;
              window.dispatchEvent(new CustomEvent<CreditLimitNotice>(CREDIT_LIMIT_EVENT, {
                detail: {
                  message: payload.message || payload.error || 'This action is unavailable because your monthly allowance has been exhausted.',
                  billing: payload.billing || null,
                  sourceUrl,
                },
              }));
            }
          } catch (error) {
            console.warn('[APP] Failed to parse credit limit response:', error);
          }
        }
        return response;
      }) as typeof window.fetch;
      window.__rescriptCreditFetchPatched = true;
    }

    return () => {
      window.removeEventListener(CREDIT_LIMIT_EVENT, handleCreditLimitEvent as EventListener);
    };
  }, []);

  return (
    <IdleProvider>
      <DarkModeProvider>
        <AudioProvider>
          <AudioUIProvider>
            <TopicVisualizationProvider>
              <AutoScrollProvider>
                <>
                  <RuntimeThemeColorSync />
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/questions" element={<PublicQuestionExplorer />} />
                    <Route path="/session/:sessionId" element={<SessionPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/admin/comments" element={<CommentsExplorer />} />
                    <Route path="/agents" element={<AgentsPage />} />
                    <Route path="/schedules" element={<SchedulesPage />} />
                    {/* REMOVED: /public/:sessionId route - legacy route that bypassed share token security */}
                    <Route path="/public/share/:shareToken" element={<PublicShareRedirect />} />
                    {/* Short share URL for homepage video links */}
                    <Route path="/share/:shareToken" element={<PublicShareView />} />
                  </Routes>
                  {creditLimitNotice && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-4">
                      <div className="w-full max-w-lg rounded-2xl border border-red-200 bg-white p-6 shadow-2xl">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-600">Monthly allowance reached</p>
                            <h2 className="mt-2 text-xl font-semibold text-slate-900">This action is currently blocked</h2>
                          </div>
                          <button
                            type="button"
                            className="rounded-full border border-slate-200 px-3 py-1 text-sm text-slate-600 hover:bg-slate-50"
                            onClick={() => setCreditLimitNotice(null)}
                          >
                            Close
                          </button>
                        </div>
                        <p className="mt-4 text-sm leading-6 text-slate-700">
                          {creditLimitNotice.message}
                        </p>
                        {creditLimitNotice.billing && (
                          <div className="mt-5 grid gap-3 sm:grid-cols-2">
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                              <p className="text-xs uppercase tracking-wide text-slate-500">USD this month</p>
                              <p className="mt-1 text-lg font-semibold text-slate-900">
                                {formatMoney(creditLimitNotice.billing.totalCostUsd)} / {creditLimitNotice.billing.monthlyUsdLimit == null ? 'Unlimited' : formatMoney(creditLimitNotice.billing.monthlyUsdLimit)}
                              </p>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                              <p className="text-xs uppercase tracking-wide text-slate-500">Credits this month</p>
                              <p className="mt-1 text-lg font-semibold text-slate-900">
                                {formatCredits(creditLimitNotice.billing.totalCreditsUsed)} / {creditLimitNotice.billing.monthlyCreditLimit == null ? 'Unlimited' : formatCredits(creditLimitNotice.billing.monthlyCreditLimit)}
                              </p>
                            </div>
                          </div>
                        )}
                        {creditLimitNotice.billing?.period?.periodId && (
                          <p className="mt-4 text-xs uppercase tracking-wide text-slate-500">
                            Billing period {creditLimitNotice.billing.period.periodId}
                          </p>
                        )}
                        {creditLimitNotice.sourceUrl && (
                          <p className="mt-2 text-xs text-slate-500 break-all">
                            Request: {creditLimitNotice.sourceUrl}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              </AutoScrollProvider>
            </TopicVisualizationProvider>
          </AudioUIProvider>
        </AudioProvider>
      </DarkModeProvider>
    </IdleProvider>
  );
}

export default App;
